import { Component, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  calculateFleetOrderRoute,
  cancelFleetOrder,
  deleteFleetOrder,
  checkFleetOrderChanges,
  getCoDrivers,
  getDrivers,
  getDepots,
  getFleetOrder,
  getFleetOrderMap,
  getFleetOrders,
  getFoRoutes,
  getMapConfig,
  getVehicles,
  optimizeFleetOrderRoute,
  previewFleetOrderRoute,
  removeDocumentFromFleetOrder,
  resetFleetOrderStopSequence,
  saveFleetOrderStopSequence,
  updateFleetOrder,
  updateFleetOrderSnapshot,
} from "../api/client.js";

import { ConfirmDialog, Dialog } from "../components/Dialog.jsx";

import { Icon } from "../components/Icon.jsx";

import { PlanningStatusBadge, ProcessingStatusBadge } from "../components/StatusBadge.jsx";

import { RouteBadge } from "../components/RouteBadge.jsx";
import { ResponsiveFilterPanel } from "../components/ResponsiveFilterPanel.jsx";
import { SortableHeader } from "../components/SortableHeader.jsx";
import {
  FleetActiveFilterSummary,
  FleetOrdersCommandHeader,
  FleetOrdersFilterWorkbench,
  FleetOrdersKpiStrip,
  FleetOrdersListHeader,
  FleetOrdersWorkspaceTabs,
} from "../components/fleet-orders/FleetOrdersWorkspace.jsx";
import { FleetOrderShareModal } from "../components/fleet-orders/FleetOrderShareModal.jsx";
import { FleetOrderEmergencyAmendmentDialog } from "../components/fleet-orders/FleetOrderEmergencyAmendmentDialog.jsx";
import { FleetOrderOperationalOverrideDialog } from "../components/fleet-orders/FleetOrderOperationalOverrideDialog.jsx";
import { FleetOrderOperationalCorrectionDialog } from "../components/fleet-orders/FleetOrderOperationalCorrectionDialog.jsx";
import { FleetOrderManualCompletionDialog } from "../components/fleet-orders/FleetOrderManualCompletionDialog.jsx";
import { SedulurDalanMonitoringPanel } from "../components/fleet-orders/SedulurDalanMonitoringPanel.jsx";
import { FleetOrderRouteMap } from "../components/maps/FleetOrderRouteMap.jsx";
import { useSortableData } from "../hooks/useSortableData.js";

import { canShareFleetOrderMonitoring } from "../utils/fleet-order-monitoring.js";
import {
  formatDetailVolume,
  formatDetailWeight,
  formatVolume,
  formatWeight,
} from "../utils/number-format.js";

const STATUS_LABELS = {
  SCHEDULED: "Terjadwal",
  CANCELLED: "Dibatalkan",
  COMPLETED: "Selesai",
};

const READINESS_LABELS = {
  READY: "Siap",
  ATTENTION: "Perlu perhatian",
  BLOCKED: "Terblokir",
  NOT_APPLICABLE: "Tidak berlaku",
};

function isOperationalDepot(depot) {
  return (
    depot?.active === true &&
    String(depot?.coordinateStatus || "").toUpperCase() === "VALID" &&
    Number.isFinite(Number(depot?.latitude)) &&
    Number.isFinite(Number(depot?.longitude))
  );
}

function depotOptionLabel(depot) {
  const identity = [depot?.depotName, depot?.depotCode]
    .filter(Boolean)
    .join(" · ");

  if (!depot?.active) {
    return `${identity || "Depot"} · Tidak aktif`;
  }

  if (isOperationalDepot(depot)) {
    return depot?.isDefault ? `${identity} · Default` : identity;
  }

  return `${identity || "Depot"} · Koordinat belum valid`;
}

const READINESS_CHECK_LABELS = {
  DRIVER_ASSIGNED: "Driver",
  VEHICLE_ASSIGNED: "Kendaraan",
  HAS_DOCUMENTS: "Dokumen",
  HAS_SALES_ORDERS: "Dokumen",
  SHIPPING_ADDRESS_COMPLETE: "Alamat Kirim",
  AREA_COMPLETE: "Area Kirim",
  ROUTE_COMPLETE: "Rute",
  VEHICLE_CAPACITY: "Kapasitas Kendaraan",
  LOGISTICS_COMPLETE: "Data Logistik",
  SOURCE_CHANGES_REVIEWED: "Perubahan Pesanan",
  STOP_SEQUENCE_READY: "Urutan Stop",
  ROUTE_STATUS_CURRENT: "Status Jalur",
  DEPOT_COORDINATE_READY: "Koordinat Depot",
  STOP_COORDINATES_COMPLETE: "Koordinat Stop",
};

const TABS = [
  {
    id: "summary",
    label: "Ringkasan",
  },
  {
    id: "stops",
    label: "Stop & Dokumen",
  },
  {
    id: "logistics",
    label: "Logistik",
  },
  {
    id: "map",
    label: "Peta & Rute",
  },
  {
    id: "driver",
    label: "Monitoring Driver",
  },
];

const FLEET_DETAIL_PATH = /^\/fleet-orders\/([^/]+)\/?$/;
const FLEET_DETAIL_TABS = new Set(TABS.map((tab) => tab.id));

function normalizeFleetDetailTab(value) {
  return FLEET_DETAIL_TABS.has(value) ? value : "summary";
}

function readFleetDetailLocation() {
  const match = window.location.pathname.match(FLEET_DETAIL_PATH);

  if (!match) {
    return {
      id: null,
      tab: "summary",
    };
  }

  let id = match[1];

  try {
    id = decodeURIComponent(id);
  } catch {
    // Keep the raw path segment so the API can return a grounded not-found response.
  }

  return {
    id,
    tab: normalizeFleetDetailTab(new URLSearchParams(window.location.search).get("tab")),
  };
}

function buildFleetDetailUrl(id, tab = "summary") {
  return `/fleet-orders/${encodeURIComponent(id)}?tab=${normalizeFleetDetailTab(tab)}`;
}

const FLEET_LIST_STATE_KEY = "route-planning:fleet-order-list-state";

function clearFleetOrderListReturnState() {
  try {
    window.sessionStorage.removeItem(FLEET_LIST_STATE_KEY);
  } catch {
    // Fleet Order navigation must remain usable when sessionStorage is unavailable.
  }
}

function readFleetOrderListReturnState() {
  // The stored snapshot is only a temporary bridge while the user is inside a
  // Fleet Order detail. A fresh list visit / refresh must start clean instead
  // of resurrecting an older filter, tab, sort, pagination, or scroll state.
  if (!readFleetDetailLocation().id) {
    clearFleetOrderListReturnState();
    return null;
  }

  try {
    const value = window.sessionStorage.getItem(FLEET_LIST_STATE_KEY);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function saveFleetOrderListReturnState(value) {
  try {
    window.sessionStorage.setItem(FLEET_LIST_STATE_KEY, JSON.stringify(value));
  } catch {
    // Fleet Order navigation must remain usable when sessionStorage is unavailable.
  }
}

const percentageNumber = new Intl.NumberFormat("id-ID", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function normalizeStatusClass(value, fallback = "unknown") {
  return String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");
}

function getFleetStatusLabel(status) {
  return STATUS_LABELS[String(status || "").toUpperCase()] || status || "—";
}

function getReadinessLabel(status, fallback) {
  return READINESS_LABELS[String(status || "").toUpperCase()] || fallback || "Belum diperiksa";
}

function formatDate(value) {
  if (!value) return "—";

  const parsed = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function formatTimestamp(value) {
  if (!value) return "—";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function formatCoordinate(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return "—";
  }

  return numeric.toFixed(7).replace(/0+$/, "").replace(/\.$/, "");
}

function formatCoordinatePair(latitude, longitude) {
  if (
    latitude === null ||
    latitude === undefined ||
    longitude === null ||
    longitude === undefined
  ) {
    return "—";
  }

  return `${formatCoordinate(latitude)}, ${formatCoordinate(longitude)}`;
}

function normalizeCoordinateStatus(value) {
  const normalized = String(value || "MISSING").toUpperCase();

  return [
    "VALID",
    "NEEDS_REVIEW",
    "SUSPECTED_SWAPPED",
    "OUT_OF_RANGE",
    "INCOMPLETE",
    "MISSING",
  ].includes(normalized)
    ? normalized
    : "MISSING";
}

function getCoordinateStatusLabel(value) {
  const normalized = normalizeCoordinateStatus(value);

  return {
    VALID: "Valid",
    NEEDS_REVIEW: "Perlu diperiksa",
    SUSPECTED_SWAPPED: "Diduga tertukar",
    OUT_OF_RANGE: "Di luar rentang",
    INCOMPLETE: "Belum lengkap",
    MISSING: "Belum tersedia",
  }[normalized];
}

function getCoordinateSourceLabel(value) {
  const normalized = String(value || "").toUpperCase();

  const labels = {
    SHIPPING_ADDRESS_TAG: "Snapshot Alamat Kirim",
    CUSTOMER_DEFAULT_FALLBACK: "Fallback Customer",
    CUSTOMER_DEFAULT: "Customer Default",
    MULTIPLE_SOURCES: "Beberapa sumber",
    TAG_AND_CUSTOMER_MISSING: "Belum tersedia",
    CUSTOMER_MISSING: "Belum tersedia",
    MANUAL: "Manual",
    IMPORT: "Import",
    ACCURATE: "Accurate",
    GEOCODED: "Geocoded",
    MISSING: "Belum tersedia",
  };

  return labels[normalized] || value || "—";
}

function formatPercentage(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${percentageNumber.format(Number(value))}%`;
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function getStopId(stop, index = 0) {
  return (
    stop?.key ||
    [
      stop?.customerMasterId || stop?.customerName || "customer",
      stop?.customerShippingAddressId || stop?.addressAlias || "default-address",
      index,
    ].join("|")
  );
}

function getStopLinkIds(stop) {
  return [
    ...new Set(
      safeArray(stop?.orders)
        .map((order) => String(order?.linkId || "").trim())
        .filter(Boolean),
    ),
  ];
}

function createStopSequenceDraft(stops) {
  return safeArray(stops).map((stop, index) => ({
    ...stop,
    draftId: getStopId(stop, index),
    displaySequence: index + 1,
  }));
}

function getStopSequenceSignature(stops) {
  return createStopSequenceDraft(stops)
    .map((stop) => getStopLinkIds(stop).sort().join(","))
    .join("||");
}

function isInteractiveTarget(target) {
  return Boolean(target.closest("button, a, input, select, textarea, label, [data-row-interactive]"));
}

function FleetStatusBadge({ status }) {
  return (
    <span className={`fleet-status fleet-status--${normalizeStatusClass(status)}`}>
      {getFleetStatusLabel(status)}
    </span>
  );
}

function LogisticsBadge({ status, warningCount = 0 }) {
  const normalizedStatus = String(status || "NOT_CALCULATED").toUpperCase();

  const label =
    normalizedStatus === "COMPLETE"
      ? "Lengkap"
      : normalizedStatus === "WARNING"
        ? "Perlu diperiksa"
        : normalizedStatus === "BLOCKED"
          ? "Belum lengkap"
          : "Belum dihitung";

  return (
    <span
      className={`logistics-status logistics-status--${normalizeStatusClass(
        normalizedStatus,
        "not_calculated",
      )}`}
    >
      {label}
      {warningCount ? ` · ${warningCount} peringatan` : ""}
    </span>
  );
}

function ReadinessBadge({ readiness, compact = false }) {
  const status = String(readiness?.status || "UNKNOWN").toUpperCase();

  return (
    <span
      className={[
        "fleet-readiness",
        `fleet-readiness--${normalizeStatusClass(status)}`,
        compact ? "fleet-readiness--compact" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {getReadinessLabel(status, readiness?.label)}
    </span>
  );
}

function ReadinessCheckStatus({ label: customLabel, status }) {
  const normalized = String(status || "UNKNOWN").toUpperCase();

  const label =
    customLabel ||
    (normalized === "PASS"
      ? "Sesuai"
      : normalized === "FAIL"
        ? "Perlu diperbaiki"
        : normalized === "WARNING"
          ? "Perlu diperiksa"
          : normalized === "PENDING"
            ? "Belum tersedia"
            : "Belum diperiksa");

  return (
    <span className={`readiness-check-status readiness-check-status--${normalizeStatusClass(normalized)}`}>
      {label}
    </span>
  );
}

function CoordinateStatusBadge({
  status,
  compact = false,
}) {
  const normalized = normalizeCoordinateStatus(status);

  const labels = {
    VALID: "Valid",
    NEEDS_REVIEW: "Perlu diperiksa",
    SUSPECTED_SWAPPED: "Diduga tertukar",
    OUT_OF_RANGE: "Di luar rentang",
    INCOMPLETE: "Belum lengkap",
    MISSING: "Belum tersedia",
  };
  const accessibleLabel = `Status koordinat: ${labels[normalized]}`;

  return (
    <span
      aria-label={accessibleLabel}
      title={accessibleLabel}
      className={[
        "coordinate-status",
        `coordinate-status--${normalized
          .toLowerCase()
          .replaceAll("_", "-")}`,
        compact ? "coordinate-status--compact" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {labels[normalized]}
    </span>
  );
}

function FleetMetric({ label, value, children }) {
  return (
    <div className="fleet-workbench-metric">
      <span>{label}</span>

      {children || <strong>{value}</strong>}
    </div>
  );
}

class FleetDetailTabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Fleet Order tab render failed", {
      error,
      info,
      tabId: this.props.tabId,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="fleet-workbench-panel">
        <div className="fleet-workbench-empty" role="alert">
          <strong>Konten tab belum berhasil dimuat.</strong>
          <span>Coba muat ulang tab ini atau kembali ke Ringkasan.</span>

          <div className="fleet-workbench__actions">
            <button
              className="secondary-button"
              onClick={this.handleRetry}
              type="button"
            >
              Coba Lagi
            </button>

            <button
              className="primary-button"
              onClick={this.props.onBackToSummary}
              type="button"
            >
              Kembali ke Ringkasan
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function FleetInfo({ label, value, children }) {
  return (
    <div className="fleet-workbench-info">
      <span>{label}</span>

      {children || <strong>{value || "—"}</strong>}
    </div>
  );
}

function FleetDetailContextItem({ icon, label, primary, secondary }) {
  return (
    <div className="fleet-detail-context-item">
      <span className="fleet-detail-context-item__icon" aria-hidden="true">
        <Icon name={icon} size={18} />
      </span>

      <div>
        <span>{label}</span>
        <strong>{primary || "—"}</strong>
        {secondary && <small>{secondary}</small>}
      </div>
    </div>
  );
}

function FleetSummaryMetric({ helper, icon, label, tone = "neutral", value }) {
  return (
    <article className={`fleet-summary-metric fleet-summary-metric--${tone}`}>
      <span className="fleet-summary-metric__icon" aria-hidden="true">
        <Icon name={icon} size={18} />
      </span>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {helper && <small>{helper}</small>}
      </div>
    </article>
  );
}

function ReadinessSummary({ fleetOrder, readiness }) {
  const checks = safeArray(readiness?.checks);
  const passedCount = Number.isFinite(Number(readiness?.passedCount))
    ? Number(readiness.passedCount)
    : checks.filter((item) => String(item.status || "").toUpperCase() === "PASS").length;
  const blockerCount = Number.isFinite(Number(readiness?.blockerCount))
    ? Number(readiness.blockerCount)
    : checks.filter((item) => String(item.status || "").toUpperCase() === "FAIL").length;
  const warningCount = Number.isFinite(Number(readiness?.warningCount))
    ? Number(readiness.warningCount)
    : checks.filter((item) =>
        ["WARNING", "PENDING"].includes(String(item.status || "").toUpperCase()),
      ).length;
  const totalCheckCount = Number(readiness?.totalCheckCount) || checks.length;
  const readinessPriority = {
    FAIL: 0,
    WARNING: 1,
    PENDING: 2,
    PASS: 3,
  };
  const orderedChecks = checks
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftPriority =
        readinessPriority[String(left.item.status || "").toUpperCase()] ?? 4;
      const rightPriority =
        readinessPriority[String(right.item.status || "").toUpperCase()] ?? 4;

      return leftPriority - rightPriority || left.index - right.index;
    })
    .map(({ item }) => item);

  return (
    <section className="fleet-readiness-panel">
      <header className="fleet-readiness-panel__header">
        <div>
          <span>Status Kesiapan</span>

          <div className="fleet-readiness-panel__title">
            <ReadinessBadge readiness={readiness} />

            <small>
              {blockerCount} blocker · {warningCount} perhatian
            </small>
          </div>
        </div>

        <div className="fleet-readiness-panel__score">
          <strong>{passedCount}/{totalCheckCount}</strong>

          <span>pemeriksaan sesuai</span>
        </div>
      </header>

      <div className="fleet-readiness-checks">
        {orderedChecks.map((item) => (
          <article
            className={`fleet-readiness-check fleet-readiness-check--${normalizeStatusClass(item.status)}`}
            key={item.code}
          >
            <div className="fleet-readiness-check__header">
              <strong>{READINESS_CHECK_LABELS[item.code] || item.label || item.code}</strong>

              <ReadinessCheckStatus
                label={item.statusLabel}
                status={item.status}
              />
            </div>

            <p>{item.message}</p>

            {item.affectedCount > 0 && (
              <small>
                {item.affectedCount} {item.affectedUnit || "data"} terdampak
              </small>
            )}
          </article>
        ))}

        {orderedChecks.length === 0 && (
          <div className="fleet-workbench-empty">Pemeriksaan kesiapan belum tersedia.</div>
        )}
      </div>
    </section>
  );
}

function FleetSummaryTab({
  coDrivers,
  depots,
  drivers,
  editing,
  fleetOrder,
  foRoutes,
  form,
  onFormChange,
  vehicles,
}) {
  const summary = fleetOrder.operationalSummary || {};

  const readiness = fleetOrder.readiness || {};

  if (editing) {
    return (
      <div className="fleet-workbench-panel">
        <section className="fleet-workbench-section">
          <header className="fleet-workbench-section__header">
            <div>
              <h3>Edit Fleet Order</h3>

              <p>Perbarui tanggal perjalanan, tim, kendaraan, atau rute Fleet Order.</p>
            </div>
          </header>

          <div className="fleet-order-form fleet-workbench-edit-form">
            <div className="fleet-edit-field fleet-edit-field--fo-number">
              <span>Nomor FO</span>
              <strong>{fleetOrder.foNumber}</strong>
            </div>

            <label className="fleet-edit-field fleet-edit-field--fo-date">
              <span>Tanggal FO</span>

              <input
                onChange={(event) => onFormChange("foDate", event.target.value)}
                required
                type="date"
                value={form.foDate || ""}
              />
            </label>

            <label className="fleet-edit-field fleet-edit-field--departure">
              <span>Tanggal Berangkat</span>

              <input
                onChange={(event) => onFormChange("departureDate", event.target.value)}
                required
                type="date"
                value={form.departureDate || ""}
              />
            </label>

            <label className="fleet-edit-field fleet-edit-field--return">
              <span>Estimasi Pulang</span>

              <input
                onChange={(event) => onFormChange("estimatedReturnDate", event.target.value)}
                required
                type="date"
                value={form.estimatedReturnDate || ""}
              />
            </label>

            <label className="fleet-edit-field fleet-edit-field--depot">
              <span>Depot / Gudang Asal</span>

              <select
                onChange={(event) => onFormChange("depotId", event.target.value)}
                required
                value={form.depotId || ""}
              >
                <option value="">Pilih depot asal</option>

                {depots
                  .filter(
                    (depot) =>
                      depot.active ||
                      String(depot.id) === String(form.depotId || ""),
                  )
                  .map((depot) => (
                  <option
                    disabled={!isOperationalDepot(depot)}
                    key={depot.id}
                    value={depot.id}
                  >
                    {depotOptionLabel(depot)}
                  </option>
                ))}
              </select>

              {!depots.some(isOperationalDepot) && (
                <small className="field-help field-help--warning">
                  Belum ada depot aktif dengan koordinat valid. Periksa Data Master → Depot.
                </small>
              )}
            </label>

            <label className="fleet-edit-field fleet-edit-field--driver">
              <span>Nama Driver</span>

              <select
                onChange={(event) => onFormChange("driverId", event.target.value)}
                required
                value={form.driverId || ""}
              >
                <option value="">Pilih driver</option>

                {drivers.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.driverName}
                  </option>
                ))}
              </select>
            </label>

            <label className="fleet-edit-field fleet-edit-field--co-driver">
              <span>Nama Co-Driver</span>

              <select
                onChange={(event) => onFormChange("coDriverId", event.target.value)}
                value={form.coDriverId || ""}
              >
                <option value="">Tanpa Co-driver</option>

                {coDrivers.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.coDriverName}
                  </option>
                ))}
              </select>
            </label>

            <label className="fleet-edit-field fleet-edit-field--vehicle">
              <span>Nopol Kendaraan</span>

              <select
                onChange={(event) => onFormChange("vehicleId", event.target.value)}
                required
                value={form.vehicleId || ""}
              >
                <option value="">Pilih kendaraan</option>

                {vehicles.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.vehiclePlate} · {row.vehicleType}
                  </option>
                ))}
              </select>
            </label>

            <label className="fleet-edit-field fleet-edit-field--route">
              <span>Rute FO</span>

              <select
                onChange={(event) => onFormChange("foRouteId", event.target.value)}
                required
                value={form.foRouteId || ""}
              >
                <option value="">Pilih Rute FO</option>

                {foRoutes.map((route) => (
                  <option key={route.id} value={route.id}>
                    {route.foRouteName}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="fleet-summary-workspace">
      <section className="fleet-summary-section fleet-summary-section--metrics">
        <header className="fleet-summary-section__header">
          <div>
            <span>Ringkasan Operasional</span>
            <h2>Kondisi Fleet Order saat ini</h2>
            <p>Rekap stop, dokumen, pelanggan, item, dan muatan berdasarkan snapshot aktif.</p>
          </div>

          <span className="fleet-summary-section__context">
            Snapshot: {formatTimestamp(fleetOrder.snapshotUpdatedAt || fleetOrder.updatedAt)}
          </span>
        </header>

        <div className="fleet-summary-metrics">
          <FleetSummaryMetric
            helper="Titik pengiriman"
            icon="location"
            label="Total Stop"
            value={summary.stopCount ?? fleetOrder.stopCount ?? 0}
          />

          <FleetSummaryMetric
            helper="SO/DO terhubung"
            icon="document"
            label="Total Dokumen"
            value={
              summary.documentCount ??
              summary.salesOrderCount ??
              fleetOrder.documentCount ??
              fleetOrder.salesOrderCount ??
              0
            }
          />

          <FleetSummaryMetric
            helper="Pelanggan unik"
            icon="user"
            label="Total Pelanggan"
            value={summary.customerCount ?? fleetOrder.customerCount ?? 0}
          />

          <FleetSummaryMetric
            helper="Baris item snapshot"
            icon="orders"
            label="Total Item"
            value={summary.itemCount ?? 0}
          />

          <FleetSummaryMetric
            helper="Muatan terhitung"
            icon="truck"
            label="Total Berat"
            value={formatWeight(summary.totalWeightKg ?? fleetOrder.totalWeightKg)}
          />

          <FleetSummaryMetric
            helper="Volume terhitung"
            icon="database"
            label="Total Volume"
            value={formatVolume(summary.totalVolumeM3 ?? fleetOrder.totalVolumeM3)}
          />
        </div>
      </section>

      <ReadinessSummary fleetOrder={fleetOrder} readiness={readiness} />
    </div>
  );
}

function OrderItemTable({ order }) {
  const items = safeArray(order.items);

  if (!items.length) {
    return (
      <div className="fleet-workbench-empty fleet-workbench-empty--compact">
        Belum ada snapshot item untuk pesanan ini.
      </div>
    );
  }

  return (
    <div className="fleet-stop-item-panel">
      <header>
        <div>
          <strong>Item {order.documentNumber || order.soNumber}</strong>

          <span>
            {items.length} baris item · {formatWeight(order.totalWeightKg)} ·{" "}
            {formatVolume(order.totalVolumeM3)}
          </span>
        </div>

        <LogisticsBadge
          status={order.logisticsCalculationStatus}
          warningCount={order.logisticsWarningCount}
        />
      </header>

      <div className="fleet-stop-item-table-wrap">
        <table className="fleet-stop-item-table">
          <thead>
            <tr>
              <th>Kode Barang</th>
              <th>Nama Barang</th>
              <th>Kuantitas</th>
              <th>Satuan</th>
              <th>Tipe UOM</th>
              <th>Berat</th>
              <th>Volume</th>
              <th>Validasi</th>
            </tr>
          </thead>

          <tbody>
            {items.map((item, index) => (
              <tr key={item.id || `${order.id}-${item.lineIndex}-${index}`}>
                <td>{item.skuCode || "—"}</td>
                <td>{item.itemName || "—"}</td>
                <td>{item.quantity ?? "—"}</td>
                <td>{item.unitName || "—"}</td>

                <td>
                  {item.uomType === "LARGE"
                    ? "UOM Terbesar"
                    : item.uomType === "MIDDLE"
                      ? "UOM Tengah"
                      : item.uomType === "SMALL"
                      ? "UOM Terkecil"
                      : "—"}
                </td>

                <td>
                  {formatDetailWeight(item.totalWeightKg)}
                </td>

                <td>
                  {formatDetailVolume(item.totalVolumeM3)}
                </td>

                <td>
                  {item.warningMessage ? (
                    <span className="fleet-item-warning">{item.warningMessage}</span>
                  ) : (
                    <span className="fleet-item-valid">Lengkap</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StopOrderCard({
  canCorrect,
  canRemove,
  expanded,
  fleetOrderId,
  onRemove,
  onCorrect,
  onToggleItems,
  order,
  removeDisabledReason,
}) {
  const itemCount = safeArray(order.items).length;
  const removeTitle = canRemove
    ? "Keluarkan dokumen ini dari Fleet Order."
    : canCorrect
      ? "Buka koreksi operasional untuk dokumen ini."
    : removeDisabledReason ||
      "Dokumen hanya dapat dikeluarkan ketika Fleet Order masih terjadwal.";

  return (
    <article className="fleet-stop-order">
      <div className="fleet-stop-order__main">
        <div className="fleet-stop-order__identity">
          <span>Dokumen</span>
          <strong>{order.documentNumber || order.soNumber}</strong>
          <small>{formatDate(order.orderDate)} · {itemCount} item</small>
        </div>

        <div className="fleet-stop-order__statuses">
          {order.processingStatus ? (
            <ProcessingStatusBadge label={order.processingStatusLabel} status={order.processingStatus} />
          ) : (
            <span className="status-badge status-badge--processing-unknown">
              {order.processingStatusLabel || "—"}
            </span>
          )}

          {order.planningStatus ? (
            <PlanningStatusBadge label={order.planningStatusLabel} status={order.planningStatus} />
          ) : (
            <span className="status-badge status-badge--planning-unknown">
              {order.planningStatusLabel || "—"}
            </span>
          )}

          <LogisticsBadge
            status={order.logisticsCalculationStatus}
            warningCount={order.logisticsWarningCount}
          />

          {order.hasDeliveryOrder && (
            <span className="status-badge fleet-do-status-badge">
              DO dibuat · {order.deliveryOrderCount || 1}
            </span>
          )}
        </div>

        <div className="fleet-stop-order__load">
          <div>
            <span>Berat</span>
            <strong>{formatWeight(order.totalWeightKg)}</strong>
          </div>

          <div>
            <span>Volume</span>
            <strong>{formatVolume(order.totalVolumeM3)}</strong>
          </div>
        </div>

        <div className="fleet-stop-order__actions">
          <button
            aria-expanded={expanded}
            className="table-action-button"
            onClick={onToggleItems}
            type="button"
          >
            {expanded ? "Tutup Item" : `Lihat Item (${itemCount})`}
          </button>

          <button
            aria-describedby={!canRemove && !canCorrect ? `remove-order-reason-${order.id}` : undefined}
            aria-label={`Keluarkan ${order.documentNumber || order.soNumber} dari Fleet Order`}
            className={`${canCorrect && !canRemove ? "warning-text-button" : "danger-text-button"} table-action-button`}
            disabled={!canRemove && !canCorrect}
            onClick={canRemove ? onRemove : onCorrect}
            title={removeTitle}
            type="button"
          >
            {canCorrect && !canRemove ? "Koreksi / keluarkan" : "Keluarkan dari FO"}
          </button>

          {!canRemove && !canCorrect && (
            <small
              className="fleet-stop-order__disabled-reason"
              id={`remove-order-reason-${order.id}`}
            >
              {removeDisabledReason}
            </small>
          )}
        </div>
      </div>

      {order.hasDeliveryOrder && (
        <div className="fleet-stop-order__delivery-orders">
          <div>
            <Icon name="document" size={16} />
            <strong>Delivery Order</strong>
          </div>

          <div className="fleet-stop-order__delivery-order-list">
            {safeArray(order.deliveryOrders).map((deliveryOrder) => (
              <a
                className={[
                  "fleet-stop-order__delivery-order-link",
                  deliveryOrder.reconciliationStatus === "CONFLICT"
                    ? "is-conflict"
                    : deliveryOrder.reconciliationStatus === "UNLINKED"
                      ? "is-pending"
                      : "",
                ].filter(Boolean).join(" ")}
                href={`/pembuatan-do/${encodeURIComponent(fleetOrderId)}`}
                key={deliveryOrder.id || deliveryOrder.deliveryOrderNumber}
                title={
                  deliveryOrder.reconciliationStatus === "CONFLICT"
                    ? `Delivery Order terhubung ke ${
                        deliveryOrder.activeFleetOrderNumber || "Fleet Order lain"
                      }.`
                    : deliveryOrder.reconciliationStatus === "UNLINKED"
                      ? "Delivery Order ditemukan dan akan direkonsiliasi melalui Periksa Perubahan."
                      : "Buka Pembuatan DO untuk Fleet Order ini."
                }
              >
                {deliveryOrder.deliveryOrderNumber || "Nomor DO belum tersedia"}
              </a>
            ))}
          </div>
        </div>
      )}

      {order.shippingAddressRequired && (
        <div className="fleet-stop-order__warning">
          Pesanan ini memiliki beberapa pilihan alamat, tetapi alamat kirim belum dipilih.
        </div>
      )}

      {order.hasSourceChanges && (
        <div className="fleet-stop-order__warning">
          <Icon name="warning" size={15} />
          <span>Data sumber pesanan berubah setelah snapshot Fleet Order dibuat.</span>
        </div>
      )}

      {expanded && <OrderItemTable order={order} />}
    </article>
  );
}

function FleetStopsTab({
  expandedOrders,
  expandedStops,
  fleetOrder,
  onRemoveOrder,
  onCorrectOrder,
  onToggleItems,
  onToggleStop,
}) {
  const stops = safeArray(fleetOrder.stops);
  const operationPolicy = fleetOrder.operationPolicy || {};
  const canRemove =
    fleetOrder.status === "SCHEDULED" && operationPolicy.canManageDocuments !== false;
  const canCorrect = operationPolicy.canOperationalCorrect === true;
  const removeDisabledReason =
    operationPolicy.canManageDocuments === false
      ? operationPolicy.lockReason ||
        "Dokumen tidak dapat dikeluarkan karena perjalanan sudah dimulai."
      : "Dokumen hanya dapat dikeluarkan ketika Fleet Order masih terjadwal.";
  const sequenceReady = fleetOrder.operationalSummary?.stopSequenceReady === true;

  if (!stops.length) {
    return (
      <div className="fleet-workbench-panel">
        <div className="fleet-workbench-empty">
          Fleet Order belum memiliki stop atau dokumen aktif.
        </div>
      </div>
    );
  }

  const requestedStopId = [...expandedStops][0];
  const requestedStopIndex = stops.findIndex(
    (stop, index) => getStopId(stop, index) === requestedStopId,
  );
  const selectedStopIndex = requestedStopIndex >= 0 ? requestedStopIndex : 0;
  const selectedStop = stops[selectedStopIndex];
  const selectedStopId = getStopId(selectedStop, selectedStopIndex);
  const selectedCoordinateStatus = normalizeCoordinateStatus(selectedStop.coordinateStatus);
  const selectedCoordinateWarning =
    selectedCoordinateStatus !== "VALID" ||
    selectedStop.coordinateFallback ||
    selectedStop.coordinateConflict ||
    selectedStop.coordinateSourceChanged;
  const selectedOrders = safeArray(selectedStop.orders);

  return (
    <div className="fleet-workbench-panel fleet-stop-workbench-panel">
      <section className="fleet-workbench-section fleet-stop-workbench-section">
        <header className="fleet-workbench-section__header fleet-stop-section-header">
          <div>
            <span className="fleet-section-eyebrow">STOP & DOKUMEN</span>
            <h3>Daftar tujuan dan dokumen pengiriman</h3>
            <p>
              Pilih stop untuk melihat alamat, koordinat, muatan, dan dokumen yang terhubung.
              Pengaturan urutan perjalanan dilakukan melalui tab Peta & Rute.
            </p>
          </div>

          <div className="fleet-stop-sequence-summary">
            <span
              className={[
                "fleet-stop-sequence-status",
                sequenceReady
                  ? "fleet-stop-sequence-status--ready"
                  : "fleet-stop-sequence-status--pending",
              ].join(" ")}
            >
              {sequenceReady ? "Urutan tersimpan" : "Urutan perlu disimpan"}
            </span>

            <span className="fleet-workbench-section__count">
              {stops.length} stop ·{" "}
              {fleetOrder.operationalSummary?.documentCount ??
                fleetOrder.operationalSummary?.salesOrderCount ??
                fleetOrder.documentCount ??
                fleetOrder.salesOrderCount ??
                0}{" "}
              dokumen
            </span>
          </div>
        </header>

        <div className="fleet-stop-master-detail">
          <aside aria-label="Daftar stop Fleet Order" className="fleet-stop-master-panel">
            <header className="fleet-stop-master-panel__header">
              <div>
                <strong>Daftar Stop</strong>
                <span>Pilih satu tujuan untuk melihat detail.</span>
              </div>
              <span>{stops.length}</span>
            </header>

            <div className="fleet-stop-master-list">
              {stops.map((stop, index) => {
                const stopId = getStopId(stop, index);
                const isSelected = stopId === selectedStopId;
                const coordinateStatus = normalizeCoordinateStatus(stop.coordinateStatus);
                const hasWarning =
                  stop.shippingAddressRequired ||
                  coordinateStatus !== "VALID" ||
                  stop.coordinateFallback ||
                  stop.coordinateConflict ||
                  stop.coordinateSourceChanged;

                return (
                  <button
                    aria-controls="fleet-stop-selected-detail"
                    aria-current={isSelected ? "true" : undefined}
                    className={[
                      "fleet-stop-master-row",
                      isSelected ? "is-selected" : "",
                      hasWarning ? "has-warning" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    key={stopId}
                    onClick={() => onToggleStop(stopId)}
                    type="button"
                  >
                    <span className="fleet-stop-master-row__sequence">{index + 1}</span>

                    <span className="fleet-stop-master-row__content">
                      <span className="fleet-stop-master-row__identity">
                        <strong>{stop.customerName}</strong>
                        <small>
                          {stop.addressAlias || "Alamat default"} ·{" "}
                          {stop.resolvedArea || "Area belum tersedia"}
                        </small>
                      </span>

                      <span className="fleet-stop-master-row__meta">
                        <RouteBadge
                          color={stop.resolvedRouteColor}
                          name={stop.resolvedRouteName || stop.resolvedRouteCode}
                        />
                        <CoordinateStatusBadge compact status={stop.coordinateStatus} />
                      </span>

                      <span className="fleet-stop-master-row__numbers">
                        <small>
                          {stop.documentCount ?? stop.salesOrderCount ?? 0} dokumen ·{" "}
                          {formatWeight(stop.totalWeightKg)} ·{" "}
                          {formatVolume(stop.totalVolumeM3)}
                        </small>
                      </span>
                    </span>

                    <span aria-hidden="true" className="fleet-stop-master-row__chevron">›</span>
                  </button>
                );
              })}
            </div>
          </aside>

          <section
            aria-labelledby="fleet-stop-selected-title"
            className="fleet-stop-detail-panel"
            id="fleet-stop-selected-detail"
          >
            <header className="fleet-stop-detail-panel__header">
              <div className="fleet-stop-detail-panel__identity">
                <span className="fleet-stop-detail-panel__sequence">{selectedStopIndex + 1}</span>
                <div>
                  <span>STOP {selectedStopIndex + 1}</span>
                  <h4 id="fleet-stop-selected-title">{selectedStop.customerName}</h4>
                  <p>
                    {selectedStop.addressAlias || "Alamat default"} ·{" "}
                    {selectedStop.resolvedArea || "Area belum tersedia"}
                  </p>
                </div>
              </div>

              <div className="fleet-stop-detail-panel__badges">
                <RouteBadge
                  color={selectedStop.resolvedRouteColor}
                  name={selectedStop.resolvedRouteName || selectedStop.resolvedRouteCode}
                />
                <CoordinateStatusBadge status={selectedStop.coordinateStatus} />
              </div>
            </header>

            <div className="fleet-stop-detail-metrics">
              <div>
                <span>Dokumen</span>
                <strong>{selectedStop.documentCount ?? selectedStop.salesOrderCount ?? 0}</strong>
              </div>
              <div>
                <span>Item</span>
                <strong>{selectedStop.itemCount || 0}</strong>
              </div>
              <div>
                <span>Berat</span>
                <strong>{formatWeight(selectedStop.totalWeightKg)}</strong>
              </div>
              <div>
                <span>Volume</span>
                <strong>{formatVolume(selectedStop.totalVolumeM3)}</strong>
              </div>
            </div>

            {selectedStop.shippingAddressRequired && (
              <div className="fleet-stop-detail-alert fleet-stop-detail-alert--warning">
                <Icon name="warning" size={17} />
                <div>
                  <strong>Alamat kirim belum dipilih</strong>
                  <span>Stop ini memiliki dokumen yang masih membutuhkan pilihan alamat kirim.</span>
                </div>
              </div>
            )}

            {selectedCoordinateWarning && (
              <div
                className={[
                  "fleet-stop-detail-alert",
                  selectedCoordinateStatus === "MISSING"
                    ? "fleet-stop-detail-alert--danger"
                    : "fleet-stop-detail-alert--warning",
                ].join(" ")}
              >
                <Icon name="location" size={17} />
                <div>
                  <strong>
                    {selectedCoordinateStatus === "MISSING"
                      ? "Koordinat stop belum tersedia"
                      : `Koordinat stop: ${getCoordinateStatusLabel(
                          selectedCoordinateStatus,
                        )}`}
                  </strong>
                  <span>
                    {selectedStop.coordinateConflict
                      ? "Beberapa dokumen dalam stop ini menghasilkan titik koordinat berbeda."
                      : selectedStop.coordinateFallback
                        ? "Alamat kirim belum memiliki koordinat sehingga sistem memakai koordinat default pelanggan."
                        : selectedStop.coordinateSourceChanged
                          ? "Koordinat master alamat kirim berubah setelah snapshot dokumen disimpan."
                          : selectedStop.coordinateMissingReason ||
                            "Periksa master Pelanggan atau Alamat Kirim."}
                  </span>

                  {(selectedStop.rawLatitude !== null &&
                    selectedStop.rawLatitude !== undefined) ||
                  (selectedStop.rawLongitude !== null &&
                    selectedStop.rawLongitude !== undefined) ? (
                    <small className="fleet-coordinate-raw-value">
                      Nilai Accurate: {formatCoordinatePair(
                        selectedStop.rawLatitude,
                        selectedStop.rawLongitude,
                      )}
                    </small>
                  ) : null}

                  <a
                    className="fleet-coordinate-master-link"
                    href={`/data-master?tab=customers&search=${encodeURIComponent(
                      selectedStop.coordinateCustomerNo ||
                        selectedStop.customerName ||
                        "",
                    )}`}
                  >
                    Buka Data Master Pelanggan
                  </a>
                </div>
              </div>
            )}

            <section className="fleet-stop-detail-section">
              <header>
                <div>
                  <span>INFORMASI TUJUAN</span>
                  <h5>Alamat dan koordinat</h5>
                </div>
              </header>

              <dl className="fleet-stop-detail-grid">
                <div>
                  <dt>Nama Alamat</dt>
                  <dd>{selectedStop.addressAlias || "Alamat default"}</dd>
                </div>
                <div>
                  <dt>Area Kirim</dt>
                  <dd>{selectedStop.resolvedArea || "Belum tersedia"}</dd>
                </div>
                <div>
                  <dt>Rute</dt>
                  <dd>
                    {selectedStop.resolvedRouteName ||
                      selectedStop.resolvedRouteCode ||
                      "Belum dipetakan"}
                  </dd>
                </div>
                <div>
                  <dt>Status Alamat</dt>
                  <dd>
                    {selectedStop.shippingAddressTagged
                      ? "Alamat dokumen tersimpan"
                      : selectedStop.shippingAddressRequired
                        ? "Perlu pilih alamat"
                        : "Mengikuti default"}
                  </dd>
                </div>
                <div>
                  <dt>Koordinat Efektif</dt>
                  <dd>{formatCoordinatePair(selectedStop.latitude, selectedStop.longitude)}</dd>
                </div>
                {selectedCoordinateStatus !== "VALID" && (
                  <div>
                    <dt>Koordinat Accurate</dt>
                    <dd>{formatCoordinatePair(
                      selectedStop.rawLatitude,
                      selectedStop.rawLongitude,
                    )}</dd>
                  </div>
                )}
                <div>
                  <dt>Sumber Koordinat</dt>
                  <dd>{getCoordinateSourceLabel(selectedStop.coordinateSource)}</dd>
                </div>
                <div>
                  <dt>Status Koordinat</dt>
                  <dd><CoordinateStatusBadge status={selectedStop.coordinateStatus} /></dd>
                </div>
                <div>
                  <dt>Verifikasi</dt>
                  <dd>{formatTimestamp(selectedStop.coordinateVerifiedAt)}</dd>
                </div>
              </dl>
            </section>

            <section className="fleet-stop-detail-section fleet-stop-documents-section">
              <header>
                <div>
                  <span>DOKUMEN PENGIRIMAN</span>
                  <h5>Dokumen pada stop ini</h5>
                </div>
                <strong>{selectedOrders.length} dokumen</strong>
              </header>

              <div className="fleet-stop-orders">
                {selectedOrders.map((order) => {
                  const orderId = order.linkId || order.id;

                  return (
                    <StopOrderCard
                      canCorrect={canCorrect}
                      canRemove={canRemove}
                      expanded={expandedOrders.has(orderId)}
                      fleetOrderId={fleetOrder.id}
                      key={orderId}
                      onRemove={() => onRemoveOrder(order)}
                      onCorrect={() => onCorrectOrder(order)}
                      onToggleItems={() => onToggleItems(orderId)}
                      order={order}
                      removeDisabledReason={removeDisabledReason}
                    />
                  );
                })}
              </div>
            </section>
          </section>
        </div>
      </section>
    </div>
  );
}

function UtilizationCard({ capacity, exceeded, label, total, utilization, valueFormatter }) {
  const safeUtilization =
    utilization === null || utilization === undefined ? null : Math.max(0, Number(utilization) || 0);

  const visualWidth = safeUtilization === null ? 0 : Math.min(100, safeUtilization);

  return (
    <article
      className={["fleet-utilization-card", exceeded ? "fleet-utilization-card--exceeded" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <header>
        <span>{label}</span>

        <strong>{safeUtilization === null ? "Belum tersedia" : formatPercentage(safeUtilization)}</strong>
      </header>

      <div className="fleet-utilization-values">
        <div>
          <span>Muatan</span>
          <strong>{valueFormatter(total)}</strong>
        </div>

        <div>
          <span>Kapasitas</span>
          <strong>{valueFormatter(capacity)}</strong>
        </div>
      </div>

      <div className="fleet-utilization-track">
        <span
          style={{
            width: `${visualWidth}%`,
          }}
        />
      </div>

      <small>
        {exceeded
          ? "Muatan melebihi kapasitas kendaraan."
          : safeUtilization === null
            ? "Data kapasitas kendaraan belum lengkap."
            : "Muatan masih berada dalam kapasitas kendaraan."}
      </small>
    </article>
  );
}

function FleetLogisticsTab({ fleetOrder }) {
  const issues = fleetOrder.logisticsIssues || {};

  const readinessMetrics = fleetOrder.readiness?.metrics || {};

  const categories = safeArray(issues.categories);

  const issueItems = safeArray(issues.items);

  return (
    <div className="fleet-workbench-panel">
      <section className="fleet-workbench-section">
        <header className="fleet-workbench-section__header">
          <div>
            <h3>Kapasitas dan Utilisasi</h3>

            <p>Perbandingan muatan hasil snapshot FO dengan kapasitas kendaraan.</p>
          </div>

          <LogisticsBadge
            status={issues.status || fleetOrder.logisticsCalculationStatus}
          />
        </header>

        <div className="fleet-utilization-grid">
          <UtilizationCard
            capacity={readinessMetrics.vehicleCapacityKg}
            exceeded={readinessMetrics.weightExceeded}
            label="Utilisasi Berat"
            total={readinessMetrics.totalWeightKg ?? fleetOrder.totalWeightKg}
            utilization={readinessMetrics.weightUtilizationPercent}
            valueFormatter={formatWeight}
          />

          <UtilizationCard
            capacity={readinessMetrics.vehicleVolumeM3}
            exceeded={readinessMetrics.volumeExceeded}
            label="Utilisasi Volume"
            total={readinessMetrics.totalVolumeM3 ?? fleetOrder.totalVolumeM3}
            utilization={readinessMetrics.volumeUtilizationPercent}
            valueFormatter={formatVolume}
          />
        </div>
      </section>

      <section className="fleet-workbench-section">
        <header className="fleet-workbench-section__header">
          <div>
            <h3>Rincian Permasalahan Logistik</h3>

            <p>Pengelompokan peringatan berdasarkan SKU, UOM, berat, volume, dan data item.</p>
          </div>

          <span className="fleet-workbench-section__count">
            {issues.warningCount || 0} peringatan · {issues.affectedItemCount || 0} item · {issues.affectedOrderCount || 0} dokumen
          </span>
        </header>

        {categories.length > 0 ? (
          <div className="fleet-logistics-categories">
            {categories.map((category) => (
              <article key={category.code}>
                <span>{category.label}</span>
                <strong>{category.count}</strong>
              </article>
            ))}
          </div>
        ) : (
          <div className="fleet-workbench-success">
            Tidak ada kategori permasalahan logistik yang terdeteksi.
          </div>
        )}
      </section>

      <section className="fleet-workbench-section fleet-logistics-table-section">
        <header className="fleet-workbench-section__header">
          <div>
            <h3>Item yang Perlu Ditindaklanjuti</h3>

            <p>Daftar item dan pesanan yang memerlukan tindak lanjut pada snapshot FO.</p>
          </div>
        </header>

        {issueItems.length > 0 ? (
          <div className="fleet-logistics-issues-wrap">
            <table className="fleet-logistics-issues">
              <thead>
                <tr>
                  <th>Nomor Pesanan</th>
                  <th>SKU</th>
                  <th>Nama Item</th>
                  <th>Kuantitas</th>
                  <th>Permasalahan</th>
                </tr>
              </thead>

              <tbody>
                {issueItems.map((item, index) => (
                  <tr key={[item.salesOrderId, item.itemId, item.skuCode, index].join("-")}>
                    <td>
                      <strong>{item.documentNumber || item.soNumber}</strong>
                    </td>

                    <td>{item.skuCode || "—"}</td>

                    <td>{item.itemName || "Level Pesanan"}</td>

                    <td>
                      {item.quantity !== null && item.quantity !== undefined
                        ? `${item.quantity} ${item.unitName || ""}`.trim()
                        : "—"}
                    </td>

                    <td>
                      <div className="fleet-logistics-issue-chips">
                        {safeArray(item.issues).length > 0 ? (
                          safeArray(item.issues).map((issue) => (
                            <span
                              className="fleet-logistics-issue-chip"
                              key={`${issue.code}-${issue.label}`}
                              title={issue.message || issue.label}
                            >
                              {issue.label}
                            </span>
                          ))
                        ) : (
                          <span className="fleet-logistics-issue-message">{item.message}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="fleet-workbench-success">
            Seluruh snapshot item FO tidak memiliki warning logistik.
          </div>
        )}
      </section>
    </div>
  );
}

function FleetMapTab({
  fleetOrder,
  onFleetOrderChanged,
  onNotify,
  onRouteDraftDirtyChange,
  sequenceLocked = false,
  sequenceLockReason = "",
}) {
  const [mapConfig, setMapConfig] = useState(null);
  const [mapPayload, setMapPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState("");

  const loadMap = useCallback(async () => {
    if (!fleetOrder?.id) return;

    setLoading(true);
    setError("");

    try {
      const [configPayload, routePayload] = await Promise.all([
        getMapConfig(),
        getFleetOrderMap(fleetOrder.id),
      ]);

      setMapConfig(configPayload);
      setMapPayload(routePayload);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [fleetOrder?.id]);

  useEffect(() => {
    loadMap();
  }, [loadMap]);

  async function recalculate(options = {}) {
    if (!fleetOrder?.id) return;

    setCalculating(true);
    setError("");

    try {
      const routePayload = await calculateFleetOrderRoute(
        fleetOrder.id,
        options,
      );

      setMapPayload(routePayload);
    } catch (requestError) {
      setError(requestError.message);

      try {
        setMapPayload(await getFleetOrderMap(fleetOrder.id));
      } catch {
        // Keep the previous map payload visible when the retry read fails.
      }
    } finally {
      setCalculating(false);
    }
  }

  async function optimizeRoute() {
    if (!fleetOrder?.id) return null;

    return optimizeFleetOrderRoute(fleetOrder.id);
  }

  async function previewRoute(orderedStopKeys) {
    if (!fleetOrder?.id) return null;

    return previewFleetOrderRoute(fleetOrder.id, orderedStopKeys);
  }

  async function saveRouteSequence(stops) {
    if (!fleetOrder?.id) return null;

    const payload = safeArray(stops).map((stop) => ({
      linkIds: safeArray(stop.linkIds),
    }));

    if (payload.some((stop) => !stop.linkIds.length)) {
      throw new Error("Satu atau lebih stop tidak memiliki pesanan aktif.");
    }

    const response = await saveFleetOrderStopSequence(fleetOrder.id, payload);

    onFleetOrderChanged?.(response.fleetOrder);
    onNotify?.(response.message);

    try {
      await calculateFleetOrderRoute(fleetOrder.id, {
        force: true,
      });
    } catch (requestError) {
      setError(
        `${response.message} Namun jalur final perlu dihitung ulang: ${requestError.message}`,
      );
    }

    const routePayload = await getFleetOrderMap(fleetOrder.id);
    setMapPayload(routePayload);

    return {
      ...response,
      mapPayload: routePayload,
    };
  }

  return (
    <div className="fleet-workbench-panel">
      {loading && !mapPayload ? (
        <div className="fleet-map-loading">
          <span className="loading-spinner" />
          Memuat peta Fleet Order…
        </div>
      ) : (
        <FleetOrderRouteMap
          calculating={calculating || loading}
          error={error}
          mapConfig={mapConfig}
          onOptimize={optimizeRoute}
          onPreview={previewRoute}
          onRecalculate={recalculate}
          onRetry={recalculate}
          onDraftDirtyChange={onRouteDraftDirtyChange}
          onSaveSequence={saveRouteSequence}
          payload={mapPayload}
          sequenceLockReason={sequenceLockReason}
          sequenceLocked={sequenceLocked}
        />
      )}
    </div>
  );
}

function FleetMapPlaceholderTab({ fleetOrder }) {
  const stopCount =
    fleetOrder.operationalSummary?.stopCount ??
    fleetOrder.stopCount ??
    safeArray(fleetOrder.stops).length;

  const sequenceReady = fleetOrder.operationalSummary?.stopSequenceReady === true;

  const coordinateSummary =
    fleetOrder.coordinateSummary || {};

  const depot =
    fleetOrder.depot || null;

  const coordinatesReady =
    coordinateSummary.coordinatesReady === true;

  return (
    <div className="fleet-workbench-panel">
      <section className="fleet-map-placeholder fleet-map-placeholder--coordinate-ready">
        <div className="fleet-map-placeholder__visual">
          <Icon name="route" size={34} />
        </div>

        <div>
          <span className="fleet-map-placeholder__eyebrow">
            Fondasi Koordinat
          </span>

          <h3>
            {coordinatesReady
              ? "Fondasi koordinat siap untuk pemasangan peta"
              : "Lengkapi koordinat sebelum memasang peta"}
          </h3>

          <p>
            Peta dan optimasi rute belum tersedia. Pastikan depot default dan
            seluruh stop memiliki titik koordinat yang dapat dipakai secara konsisten.
          </p>

          <div className="fleet-map-placeholder__metrics fleet-map-placeholder__metrics--coordinates">
            <FleetMetric
              label="Jumlah Stop"
              value={stopCount}
            />

            <FleetMetric
              label="Urutan Stop"
              value={
                sequenceReady
                  ? "Tersedia"
                  : "Belum disimpan"
              }
            />

            <FleetMetric
              label="Koordinat Valid"
              value={
                coordinateSummary.validStopCount ??
                0
              }
            />

            <FleetMetric
              label="Perlu Diperiksa"
              value={
                coordinateSummary.reviewStopCount ??
                0
              }
            />

            <FleetMetric
              label="Belum Tersedia"
              value={
                coordinateSummary.missingStopCount ??
                0
              }
            />

            <FleetMetric
              label="Kesiapan Peta"
              value={
                coordinatesReady
                  ? "Siap"
                  : "Belum siap"
              }
            />
          </div>
        </div>
      </section>

      <section className="fleet-workbench-section">
        <header className="fleet-workbench-section__header">
          <div>
            <h3>Depot Default</h3>

            <p>
              Titik awal dan akhir perjalanan Fleet Order.
            </p>
          </div>

          <CoordinateStatusBadge
            status={
              depot?.coordinateStatus ||
              "MISSING"
            }
          />
        </header>

        <div className="fleet-coordinate-depot">
          <div>
            <span>Nama Depot</span>

            <strong>
              {depot?.depotName ||
                "Depot default belum ditentukan"}
            </strong>
          </div>

          <div>
            <span>Kode</span>

            <strong>
              {depot?.depotCode || "—"}
            </strong>
          </div>

          <div>
            <span>Koordinat</span>

            <strong>
              {formatCoordinatePair(
                depot?.latitude,
                depot?.longitude,
              )}
            </strong>
          </div>

          <div>
            <span>Alamat</span>

            <strong>
              {depot?.addressText || "—"}
            </strong>
          </div>
        </div>
      </section>

      <section className="fleet-workbench-section">
        <header className="fleet-workbench-section__header">
          <div>
            <h3>Kesiapan Koordinat Stop</h3>

            <p>
              Koordinat efektif mengikuti snapshot Alamat Kirim, lalu
              fallback ke koordinat default pelanggan.
            </p>
          </div>

          <span className="fleet-workbench-section__count">
            {coordinateSummary.validStopCount || 0} valid ·{" "}
            {coordinateSummary.reviewStopCount || 0} review ·{" "}
            {coordinateSummary.missingStopCount || 0} missing
          </span>
        </header>

        <div className="fleet-coordinate-stop-list">
          {safeArray(fleetOrder.stops).map(
            (stop, index) => (
              <article
                className={`fleet-coordinate-stop fleet-coordinate-stop--${normalizeCoordinateStatus(
                  stop.coordinateStatus,
                )
                  .toLowerCase()
                  .replaceAll("_", "-")}`}
                key={
                  stop.key ||
                  `${stop.customerName}-${index}`
                }
              >
                <span className="fleet-coordinate-stop__sequence">
                  {stop.displaySequence ||
                    index + 1}
                </span>

                <div className="fleet-coordinate-stop__identity">
                  <strong>
                    {stop.customerName}
                  </strong>

                  <span>
                    {stop.addressAlias ||
                      "Alamat default"}
                    {" · "}
                    {stop.resolvedArea ||
                      "Area belum tersedia"}
                  </span>
                </div>

                <div className="fleet-coordinate-stop__point">
                  <span>Koordinat</span>

                  <strong>
                    {formatCoordinatePair(
                      stop.latitude,
                      stop.longitude,
                    )}
                  </strong>
                </div>

                <div className="fleet-coordinate-stop__source">
                  <span>Sumber</span>

                  <strong>
                    {getCoordinateSourceLabel(
                      stop.coordinateSource,
                    )}
                  </strong>
                </div>

                <CoordinateStatusBadge
                  status={
                    stop.coordinateStatus
                  }
                />
              </article>
            ),
          )}

          {safeArray(fleetOrder.stops).length === 0 && (
            <div className="fleet-workbench-empty">
              Fleet Order belum memiliki stop.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}


const FLEET_ORDER_EDITABLE_FIELDS = [
  "foDate",
  "departureDate",
  "estimatedReturnDate",
  "depotId",
  "driverId",
  "coDriverId",
  "vehicleId",
  "foRouteId",
];

function hasFleetOrderFormChanges(fleetOrder, form) {
  if (!fleetOrder || !form) return false;

  return FLEET_ORDER_EDITABLE_FIELDS.some(
    (field) => String(form[field] ?? "") !== String(fleetOrder[field] ?? ""),
  );
}

function FleetOrderDetailWorkspace({
  id,
  initialTab = "summary",
  onClose,
  onShare,
  onTabChange,
  onUpdated,
}) {
  const [fleetOrder, setFleetOrder] = useState(null);

  const [drivers, setDrivers] = useState([]);

  const [depots, setDepots] = useState([]);

  const [coDrivers, setCoDrivers] = useState([]);

  const [vehicles, setVehicles] = useState([]);

  const [foRoutes, setFoRoutes] = useState([]);

  const [form, setForm] = useState(null);

  const [activeTab, setActiveTab] = useState(() => normalizeFleetDetailTab(initialTab));

  const [editing, setEditing] = useState(false);

  const [routeDraftDirty, setRouteDraftDirty] = useState(false);
  const skipNextNavigationGuardRef = useRef(false);

  const [saving, setSaving] = useState(false);

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const [cancelling, setCancelling] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletionReason, setDeletionReason] = useState("");
  const [emergencyAmendmentOpen, setEmergencyAmendmentOpen] = useState(false);
  const [operationalOverrideOpen, setOperationalOverrideOpen] = useState(false);
  const [operationalCorrection, setOperationalCorrection] = useState(null);
  const [manualCompletionOpen, setManualCompletionOpen] = useState(false);

  const [error, setError] = useState("");

  const [checkingChanges, setCheckingChanges] = useState(false);

  const [updatingSnapshot, setUpdatingSnapshot] = useState(false);

  const [comparison, setComparison] = useState(null);

  const [changeCheckNotice, setChangeCheckNotice] = useState("");

  const [expandedStops, setExpandedStops] = useState(new Set());

  const [expandedOrders, setExpandedOrders] = useState(new Set());

  const [removingOrder, setRemovingOrder] = useState(null);

  const [removing, setRemoving] = useState(false);

  const [sequenceEditing, setSequenceEditing] = useState(false);

  const [sequenceStops, setSequenceStops] = useState([]);

  const [sequenceSaving, setSequenceSaving] = useState(false);

  const [sequenceResetting, setSequenceResetting] = useState(false);

  const [resetSequenceConfirmOpen, setResetSequenceConfirmOpen] = useState(false);

  const cancelled = fleetOrder?.status === "CANCELLED";
  const operationPolicy = fleetOrder?.operationPolicy || {};
  const planningLocked = operationPolicy.planningLocked === true;
  const planningLockReason =
    operationPolicy.lockReason ||
    "Fleet Order tidak dapat diubah karena perjalanan sudah dimulai.";

  const canEdit =
    Boolean(fleetOrder) &&
    !cancelled &&
    !fleetOrder?.deletedAt &&
    operationPolicy.canEdit !== false;
  const canCancel =
    Boolean(fleetOrder) &&
    fleetOrder.status === "SCHEDULED" &&
    operationPolicy.canCancel !== false;
  const canDelete =
    Boolean(fleetOrder) &&
    !fleetOrder?.deletedAt &&
    operationPolicy.canDelete !== false;
  const canUpdateSnapshot = operationPolicy.canUpdateSnapshot !== false;
  const formDirty = hasFleetOrderFormChanges(fleetOrder, form);
  const editDisabledReason = fleetOrder?.deletedAt
    ? "Fleet Order yang sudah dihapus tidak dapat diedit."
    : cancelled
      ? "Fleet Order yang sudah dibatalkan tidak dapat diedit."
      : operationPolicy.canEdit === false
        ? planningLockReason
        : "Edit data Fleet Order.";

  const monitoringShareAvailability = fleetOrder
    ? canShareFleetOrderMonitoring(fleetOrder)
    : {
        allowed: false,
        reason: "Data Fleet Order belum tersedia.",
      };

  useEffect(() => {
    let active = true;

    setFleetOrder(null);
    setForm(null);
    setError("");
    setEditing(false);
    setRouteDraftDirty(false);
    setActiveTab(normalizeFleetDetailTab(initialTab));
    setComparison(null);
    setChangeCheckNotice("");
    setExpandedStops(new Set());
    setExpandedOrders(new Set());
    setSequenceEditing(false);
    setSequenceStops([]);
    setResetSequenceConfirmOpen(false);
    setEmergencyAmendmentOpen(false);
    setOperationalOverrideOpen(false);
    setOperationalCorrection(null);

    Promise.all([
      getFleetOrder(id),
      getDrivers(),
      getDepots(),
      getCoDrivers(),
      getVehicles(),
      getFoRoutes(),
    ])
      .then(([
        fleetPayload,
        driverPayload,
        depotPayload,
        coDriverPayload,
        vehiclePayload,
        foRoutePayload,
      ]) => {
        if (!active) return;

        setFleetOrder(fleetPayload.fleetOrder);

        setForm(fleetPayload.fleetOrder);

        setDrivers(driverPayload.drivers || []);

        setDepots(depotPayload.depots || []);

        setCoDrivers(coDriverPayload.coDrivers || []);

        setVehicles(vehiclePayload.vehicles || []);

        setFoRoutes(foRoutePayload.foRoutes || []);

        setSequenceStops(createStopSequenceDraft(fleetPayload.fleetOrder.stops));

        const firstStop = safeArray(fleetPayload.fleetOrder.stops)[0];

        if (firstStop) {
          setExpandedStops(new Set([getStopId(firstStop, 0)]));
        }
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.message);
        }
      });

    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    setActiveTab(normalizeFleetDetailTab(initialTab));
  }, [initialTab]);

  function applyFleetOrder(nextFleetOrder) {
    setFleetOrder(nextFleetOrder);
    setForm(nextFleetOrder);
    setSequenceStops(createStopSequenceDraft(nextFleetOrder?.stops));
    setSequenceEditing(false);
  }

  function handleEmergencyAmendmentApplied(response) {
    applyFleetOrder(response.fleetOrder);
    setEmergencyAmendmentOpen(false);
    setActiveTab("summary");
    onTabChange?.("summary");
    onUpdated(response.message);
  }

  function handleOperationalOverrideApplied(response) {
    setOperationalOverrideOpen(false);
    onUpdated(response.message);

    if (response.action === "CREATE_SUPPLEMENTAL_FO" && response.supplementalFleetOrder?.id) {
      window.location.assign(
        `/fleet-orders/${encodeURIComponent(response.supplementalFleetOrder.id)}?tab=summary`,
      );
      return;
    }

    if (response.fleetOrder) {
      applyFleetOrder(response.fleetOrder);
    }
    setActiveTab("summary");
    onTabChange?.("summary");
    if (response.openEmergencyAmendment) {
      setEmergencyAmendmentOpen(true);
    }
  }

  function handleOperationalCorrectionApplied(response) {
    setOperationalCorrection(null);
    if (response.fleetOrder) applyFleetOrder(response.fleetOrder);
    onUpdated(response.message);
  }


  function handleManualCompletion(response) {
    setManualCompletionOpen(false);
    if (response.fleetOrder) {
      applyFleetOrder(response.fleetOrder);
    }
    onUpdated(
      `${fleetOrder.foNumber} berhasil diselesaikan manual.`,
    );
  }

  function updateForm(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function save(event) {
    event.preventDefault();

    if (!formDirty || saving) return;

    setSaving(true);
    setError("");

    try {
      const response = await updateFleetOrder(id, form);

      applyFleetOrder(response.fleetOrder);

      setEditing(false);

      onUpdated(response.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  function enterEdit(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!canEdit) return;

    setForm({
      ...fleetOrder,
    });

    setError("");
    setActiveTab("summary");
    onTabChange?.("summary");
    setEditing(true);
  }

  function cancelEdit() {
    setForm({
      ...fleetOrder,
    });

    setError("");
    setEditing(false);
  }

  async function confirmCancellation() {
    setCancelling(true);
    setError("");

    try {
      const response = await cancelFleetOrder(id);

      applyFleetOrder(response.fleetOrder);

      setEditing(false);
      setCancelConfirmOpen(false);

      onUpdated(response.message);
    } catch (requestError) {
      setError(requestError.message);
      setCancelConfirmOpen(false);
    } finally {
      setCancelling(false);
    }
  }

  async function confirmDeletion() {
    setDeleting(true);
    setError("");
    try {
      const response = await deleteFleetOrder(id, deletionReason);
      applyFleetOrder(response.fleetOrder);
      setDeleteConfirmOpen(false);
      setDeletionReason("");
      onUpdated(response.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setDeleting(false);
    }
  }

  async function checkChanges() {
    setCheckingChanges(true);
    setError("");
    setChangeCheckNotice("");

    try {
      const response = await checkFleetOrderChanges(id);
      const hasChanges = response.comparison?.hasChanges === true;

      if (response.fleetOrder) {
        applyFleetOrder(response.fleetOrder);
      }

      setComparison(hasChanges ? response.comparison : null);
      setChangeCheckNotice(hasChanges ? "" : response.message);

      if (!hasChanges) {
        onUpdated(response.message);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCheckingChanges(false);
    }
  }

  async function confirmSnapshotUpdate() {
    setUpdatingSnapshot(true);
    setError("");

    try {
      const response = await updateFleetOrderSnapshot(id);

      applyFleetOrder(response.fleetOrder);

      setComparison(null);
      setChangeCheckNotice("");

      onUpdated(response.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setUpdatingSnapshot(false);
    }
  }

  function toggleStop(stopId) {
    setExpandedStops(new Set([stopId]));
    setExpandedOrders(new Set());
  }

  function toggleItems(orderId) {
    setExpandedOrders((current) => {
      const next = new Set(current);

      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }

      return next;
    });
  }

  function startSequenceEdit() {
    setSequenceStops(createStopSequenceDraft(fleetOrder?.stops));
    setSequenceEditing(true);
    setExpandedStops(new Set());
    setError("");
  }

  function cancelSequenceEdit() {
    setSequenceStops(createStopSequenceDraft(fleetOrder?.stops));
    setSequenceEditing(false);
    setError("");
  }

  function moveStop(index, direction) {
    setSequenceStops((current) => {
      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [selected] = next.splice(index, 1);
      next.splice(targetIndex, 0, selected);

      return next.map((stop, nextIndex) => ({
        ...stop,
        displaySequence: nextIndex + 1,
      }));
    });
  }

  async function saveSequence() {
    const payload = sequenceStops.map((stop) => ({
      linkIds: getStopLinkIds(stop),
    }));

    if (payload.some((stop) => !stop.linkIds.length)) {
      setError("Satu atau lebih stop tidak memiliki pesanan aktif.");
      return;
    }

    setSequenceSaving(true);
    setError("");

    try {
      const response = await saveFleetOrderStopSequence(id, payload);

      applyFleetOrder(response.fleetOrder);

      onUpdated(response.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSequenceSaving(false);
    }
  }

  async function confirmResetSequence() {
    setSequenceResetting(true);
    setError("");

    try {
      const response = await resetFleetOrderStopSequence(id);

      applyFleetOrder(response.fleetOrder);

      setResetSequenceConfirmOpen(false);
      onUpdated(response.message);
    } catch (requestError) {
      setError(requestError.message);
      setResetSequenceConfirmOpen(false);
    } finally {
      setSequenceResetting(false);
    }
  }

  async function confirmRemoveOrder() {
    setRemoving(true);
    setError("");

    try {
      const response = await removeDocumentFromFleetOrder(
        id,
        removingOrder.documentType || "SALES_ORDER",
        removingOrder.documentId || removingOrder.id,
      );

      applyFleetOrder(response.fleetOrder);

      setRemovingOrder(null);

      onUpdated(response.message);
    } catch (requestError) {
      setError(requestError.message);
      setRemovingOrder(null);
    } finally {
      setRemoving(false);
    }
  }

  const sequenceDirty =
    getStopSequenceSignature(sequenceStops) !== getStopSequenceSignature(fleetOrder?.stops);

  function confirmDiscardRouteDraft() {
    if (!routeDraftDirty) return true;

    return window.confirm(
      "Ada perubahan urutan rute yang belum disimpan. Batalkan perubahan dan lanjutkan?",
    );
  }

  useEffect(() => {
    function handleBeforeNavigate(event) {
      if (skipNextNavigationGuardRef.current) {
        skipNextNavigationGuardRef.current = false;
        return;
      }

      if (!confirmDiscardRouteDraft()) {
        event.preventDefault();
      }
    }

    function handleBeforeUnload(event) {
      if (!routeDraftDirty) return;
      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("route-planning:before-navigate", handleBeforeNavigate);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("route-planning:before-navigate", handleBeforeNavigate);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [routeDraftDirty]);

  function closeWorkbench() {
    if (confirmDiscardRouteDraft()) {
      skipNextNavigationGuardRef.current = true;
      onClose();
    }
  }

  const dialogTitle = fleetOrder ? fleetOrder.foNumber : "Detail Fleet Order";
  const contextDocumentCount =
    fleetOrder?.operationalSummary?.documentCount ??
    fleetOrder?.operationalSummary?.salesOrderCount ??
    fleetOrder?.documentCount ??
    fleetOrder?.salesOrderCount ??
    0;

  return (
    <>
      <section aria-labelledby="fleet-detail-page-title" className="fleet-detail-page">
        {!fleetOrder ? (
          <>
            <nav aria-label="Breadcrumb Fleet Order" className="fleet-detail-breadcrumb">
              <button onClick={closeWorkbench} type="button">
                <Icon name="chevronLeft" size={16} />
                Fleet Order
              </button>
              <span aria-hidden="true">/</span>
              <strong>Memuat detail</strong>
            </nav>

            <div className="fleet-detail-page__state">
              <h1 id="fleet-detail-page-title">Detail Fleet Order</h1>
              <p>{error || "Memuat detail Fleet Order…"}</p>
            </div>
          </>
        ) : (
          <form className="fleet-detail-workspace" onSubmit={save}>
            <div className="fleet-detail-topline">
              <nav aria-label="Breadcrumb Fleet Order" className="fleet-detail-breadcrumb">
                <button onClick={closeWorkbench} type="button">
                  <Icon name="chevronLeft" size={16} />
                  Fleet Order
                </button>
                <span aria-hidden="true">/</span>
                <strong>{fleetOrder.foNumber}</strong>
              </nav>

              <div className="fleet-detail-top-actions">
                {editing ? (
                  <>
                    <button
                      className="secondary-button"
                      disabled={saving}
                      onClick={cancelEdit}
                      type="button"
                    >
                      Batal Edit
                    </button>

                    <button
                      aria-describedby={!formDirty && !saving ? "fleet-save-disabled-reason" : undefined}
                      className="primary-button"
                      disabled={saving || !formDirty}
                      title={
                        saving
                          ? "Perubahan sedang disimpan."
                          : formDirty
                            ? "Simpan perubahan Fleet Order."
                            : "Belum ada perubahan yang perlu disimpan."
                      }
                      type="submit"
                    >
                      {saving ? "Menyimpan…" : "Simpan Perubahan"}
                    </button>
                    {!formDirty && !saving && (
                      <span className="fleet-sr-only" id="fleet-save-disabled-reason">
                        Belum ada perubahan yang perlu disimpan.
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {!cancelled &&
                      !fleetOrder.hasSourceChanges &&
                      safeArray(fleetOrder.orders).length > 0 && (
                        <button
                          className="secondary-button"
                          disabled={checkingChanges}
                          onClick={checkChanges}
                          type="button"
                        >
                          {checkingChanges ? "Memeriksa…" : "Periksa Perubahan"}
                        </button>
                      )}

                    {!fleetOrder.deletedAt && (
                      <a
                        className="secondary-button fleet-detail-do-link"
                        href={`/pembuatan-do/${encodeURIComponent(fleetOrder.id)}`}
                        title="Buka workspace Pembuatan DO untuk Fleet Order ini."
                      >
                        Pembuatan DO
                      </a>
                    )}

                    {!fleetOrder.deletedAt && canEdit && (
                      <button
                        className="primary-button"
                        onClick={enterEdit}
                        title="Edit Fleet Order."
                        type="button"
                      >
                        Edit FO
                      </button>
                    )}

                    {!fleetOrder.deletedAt && (
                      <details className="fleet-detail-more-menu">
                        <summary aria-label="Tindakan Fleet Order">Tindakan</summary>

                        <div className="fleet-detail-more-menu__panel">
                          <button
                            disabled={!monitoringShareAvailability.allowed}
                            onClick={() => onShare?.(fleetOrder)}
                            title={
                              monitoringShareAvailability.allowed
                                ? "Bagikan monitoring Fleet Order ke WhatsApp."
                                : monitoringShareAvailability.reason
                            }
                            type="button"
                          >
                            Bagikan Monitoring
                          </button>

                          {!fleetOrder.deletedAt && fleetOrder.status === "SCHEDULED" && (
                            <button
                              disabled={!operationPolicy.canEmergencyAmend}
                              onClick={() => setEmergencyAmendmentOpen(true)}
                              title={
                                operationPolicy.canEmergencyAmend
                                  ? "Tambahkan Sales Order urgent sebelum titik pengiriman dimulai."
                                  : operationPolicy.emergencyAmendmentReason || "Perubahan darurat tidak tersedia."
                              }
                              type="button"
                            >
                              Tambahkan Pesanan Darurat
                            </button>
                          )}

                          {!fleetOrder.deletedAt && operationPolicy.canOperationalCorrect && (
                            <button
                              className="is-warning"
                              onClick={() => setOperationalCorrection({})}
                              title="Perbaiki koordinat atau lepaskan pengiriman tidak berhasil tanpa membuka edit biasa."
                              type="button"
                            >
                              Koreksi Operasional
                            </button>
                          )}

                          {!fleetOrder.deletedAt && planningLocked && (
                            <button
                              className="is-warning"
                              onClick={() => setOperationalOverrideOpen(true)}
                              title="Gunakan hak khusus untuk membatalkan salah pengoperasian dan membuka kembali penambahan Sales Order."
                              type="button"
                            >
                              Reset Eksekusi Fleet Order
                            </button>
                          )}


                          {!fleetOrder.deletedAt && fleetOrder.status === "SCHEDULED" && (
                            <button
                              className="is-warning"
                              onClick={() => setManualCompletionOpen(true)}
                              title="Tutup Fleet Order secara manual setelah seluruh titik memiliki hasil akhir."
                              type="button"
                            >
                              Selesaikan Fleet Order
                            </button>
                          )}

                          {!fleetOrder.deletedAt && fleetOrder.status === "SCHEDULED" && (
                            <button
                              disabled={!canCancel}
                              onClick={() => setCancelConfirmOpen(true)}
                              title={canCancel ? "Batalkan Fleet Order." : planningLockReason}
                              type="button"
                            >
                              Batalkan Fleet Order
                            </button>
                          )}

                          {!fleetOrder.deletedAt && (
                            <button
                              className="is-danger"
                              disabled={!canDelete}
                              onClick={() => setDeleteConfirmOpen(true)}
                              title={canDelete ? "Hapus Fleet Order." : planningLockReason}
                              type="button"
                            >
                              Hapus Fleet Order
                            </button>
                          )}
                        </div>
                      </details>
                    )}
                  </>
                )}
              </div>
            </div>

            <header className="fleet-detail-hero">
              <div className="fleet-detail-hero__identity">
                <span className="fleet-detail-hero__eyebrow">
                  {editing ? "Mengedit Fleet Order" : "Workspace Fleet Order"}
                </span>

                <div className="fleet-detail-hero__title-row">
                  <h1 id="fleet-detail-page-title">{dialogTitle}</h1>

                  <div className="fleet-detail-hero__badges">
                    <FleetStatusBadge status={fleetOrder.status} />
                    <ReadinessBadge compact readiness={fleetOrder.readiness} />
                    <RouteBadge name={fleetOrder.foRouteName || fleetOrder.routeName} />
                    {operationPolicy.executionActive && (
                      <span className="status-badge status-badge--execution-active">
                        {operationPolicy.executionStatusLabel || "Eksekusi aktif"}
                      </span>
                    )}

                    {fleetOrder.supplementalRelation?.isSupplemental && (
                      <a
                        className="status-badge status-badge--supplemental"
                        href={`/fleet-orders/${encodeURIComponent(fleetOrder.supplementalRelation.parent.id)}?tab=summary`}
                      >
                        FO Tambahan · {fleetOrder.supplementalRelation.parent.foNumber}
                      </a>
                    )}
                    {fleetOrder.deletedAt && (
                      <span className="status-badge status-badge--planning-blocked">Dihapus</span>
                    )}
                  </div>
                </div>

                <p>
                  Berangkat {formatDate(fleetOrder.departureDate)} · Estimasi pulang{" "}
                  {formatDate(fleetOrder.estimatedReturnDate)}
                </p>
              </div>

              <div className="fleet-detail-hero__updated">
                <span>Data Fleet Order diperbarui</span>
                <strong>{formatTimestamp(fleetOrder.updatedAt)}</strong>
              </div>

              <div className="fleet-detail-context-strip">
                <FleetDetailContextItem
                  icon="location"
                  label="Depot"
                  primary={
                    fleetOrder.depot?.depotName ||
                    fleetOrder.depot?.name ||
                    fleetOrder.depotName ||
                    fleetOrder.originDepotName ||
                    "Depot belum tersedia"
                  }
                  secondary={
                    fleetOrder.depot?.depotCode ||
                    fleetOrder.depotCode ||
                    fleetOrder.depot?.addressText ||
                    fleetOrder.depotAddress ||
                    "Titik awal perjalanan"
                  }
                />

                <FleetDetailContextItem
                  icon="clock"
                  label="Jadwal"
                  primary={formatDate(fleetOrder.departureDate)}
                  secondary={`FO ${formatDate(fleetOrder.foDate)} · pulang ${formatDate(
                    fleetOrder.estimatedReturnDate,
                  )}`}
                />

                <FleetDetailContextItem
                  icon="truck"
                  label="Armada"
                  primary={fleetOrder.vehiclePlate || "—"}
                  secondary={fleetOrder.vehicleType || "Jenis kendaraan belum tersedia"}
                />

                <FleetDetailContextItem
                  icon="user"
                  label="Tim"
                  primary={fleetOrder.driverName || "—"}
                  secondary={
                    fleetOrder.coDriverName
                      ? `Co-driver: ${fleetOrder.coDriverName}`
                      : "Co-driver belum dipilih"
                  }
                />

                <FleetDetailContextItem
                  icon="route"
                  label="Rute FO"
                  primary={getFleetRouteName(fleetOrder)}
                  secondary={`${fleetOrder.stopCount || 0} stop · ${contextDocumentCount} dokumen`}
                />

                <FleetDetailContextItem
                  icon="database"
                  label="Kapasitas Armada"
                  primary={
                    fleetOrder.vehicleCapacityKg !== null &&
                    fleetOrder.vehicleCapacityKg !== undefined
                      ? formatWeight(fleetOrder.vehicleCapacityKg)
                      : "—"
                  }
                  secondary={
                    fleetOrder.vehicleVolumeM3 !== null &&
                    fleetOrder.vehicleVolumeM3 !== undefined
                      ? formatVolume(fleetOrder.vehicleVolumeM3)
                      : "Volume belum tersedia"
                  }
                />
              </div>
            </header>

            {operationPolicy.pendingAmendment && (
              <div className="fleet-amendment-pending-banner" role="status">
                <span className="fleet-amendment-pending-banner__icon" aria-hidden="true">
                  <Icon name="warning" size={19} />
                </span>
                <div>
                  <strong>Perubahan darurat revisi {operationPolicy.pendingAmendment.revisionNo}</strong>
                  <span>
                    {operationPolicy.pendingAmendment.addedDocumentCount} Sales Order tambahan · {operationPolicy.pendingAmendment.addedStopCount} stop baru · {operationPolicy.pendingAmendment.status === "PENDING_ACK" ? "menunggu konfirmasi driver" : "menunggu loading ulang"}
                  </span>
                </div>
                <span className="fleet-amendment-pending-banner__status">
                  {operationPolicy.pendingAmendment.status === "PENDING_ACK" ? "Menunggu driver" : "Loading ulang"}
                </span>
              </div>
            )}

            <div className="fleet-detail-tabs-shell">
              <div className="fleet-workbench-tabs" role="tablist">
                {TABS.map((tab) => (
                  <button
                    aria-selected={activeTab === tab.id}
                    className={activeTab === tab.id ? "is-active" : ""}
                    disabled={editing && tab.id !== "summary"}
                    key={tab.id}
                    onClick={() => {
                      if (
                        activeTab === "map" &&
                        tab.id !== "map" &&
                        !confirmDiscardRouteDraft()
                      ) {
                        return;
                      }

                      setActiveTab(tab.id);
                      onTabChange?.(tab.id);
                      setError("");
                    }}
                    role="tab"
                    type="button"
                  >
                    {tab.label}

                    {tab.id === "stops" && (
                      <span>{fleetOrder.stopCount ?? safeArray(fleetOrder.stops).length}</span>
                    )}

                    {tab.id === "logistics" && fleetOrder.logisticsWarningCount > 0 && (
                      <span>{fleetOrder.logisticsWarningCount}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {(error || fleetOrder.hasSourceChanges || changeCheckNotice) && (
              <div className="fleet-detail-notices">
                {error && (
                  <div className="error-banner" role="alert">
                    {error}
                  </div>
                )}

                {changeCheckNotice && !fleetOrder.hasSourceChanges && (
                  <div
                    className="fleet-detail-change-notice fleet-detail-change-notice--checked"
                    role="status"
                  >
                    <span className="fleet-detail-change-notice__icon" aria-hidden="true">
                      <Icon name="check" size={17} />
                    </span>

                    <div>
                      <strong>Pesanan telah diperiksa</strong>
                      <span>{changeCheckNotice}</span>
                    </div>
                  </div>
                )}

                {fleetOrder.hasSourceChanges && (
                  <div className="fleet-detail-change-notice" role="status">
                    <span className="fleet-detail-change-notice__icon" aria-hidden="true">
                      <Icon name="warning" size={17} />
                    </span>

                    <div>
                      <strong>Pesanan berubah setelah masuk Fleet Order</strong>
                      <span>Data Fleet Order belum mengikuti perubahan terbaru.</span>
                    </div>

                    {!cancelled && safeArray(fleetOrder.orders).length > 0 && (
                      <button
                        className="secondary-button"
                        disabled={checkingChanges}
                        onClick={checkChanges}
                        type="button"
                      >
                        {checkingChanges ? "Memeriksa…" : "Periksa Perubahan"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="fleet-workbench__body">
              <FleetDetailTabErrorBoundary
                key={activeTab}
                onBackToSummary={() => {
                  setActiveTab("summary");
                  onTabChange?.("summary");
                  setError("");
                }}
                tabId={activeTab}
              >
                {activeTab === "summary" && (
                  <FleetSummaryTab
                    coDrivers={coDrivers}
                    depots={depots}
                    drivers={drivers}
                    editing={editing}
                    fleetOrder={fleetOrder}
                    foRoutes={foRoutes}
                    form={form}
                    onFormChange={updateForm}
                    vehicles={vehicles}
                  />
                )}

                {activeTab === "stops" && (
                  <FleetStopsTab
                    expandedOrders={expandedOrders}
                    expandedStops={expandedStops}
                    fleetOrder={fleetOrder}
                    onCancelSequenceEdit={cancelSequenceEdit}
                    onMoveStop={moveStop}
                    onCorrectOrder={(order) => setOperationalCorrection({ initialDocument: order })}
                    onRemoveOrder={setRemovingOrder}
                    onRequestResetSequence={() => setResetSequenceConfirmOpen(true)}
                    onSaveSequence={saveSequence}
                    onStartSequenceEdit={startSequenceEdit}
                    onToggleItems={toggleItems}
                    onToggleStop={toggleStop}
                    sequenceDirty={sequenceDirty}
                    sequenceEditing={sequenceEditing}
                    sequenceResetting={sequenceResetting}
                    sequenceSaving={sequenceSaving}
                    sequenceStops={sequenceStops}
                  />
                )}

                {activeTab === "logistics" && <FleetLogisticsTab fleetOrder={fleetOrder} />}

                {activeTab === "map" && (
                  <FleetMapTab
                    fleetOrder={fleetOrder}
                    onFleetOrderChanged={applyFleetOrder}
                    onNotify={onUpdated}
                    onRouteDraftDirtyChange={setRouteDraftDirty}
                    sequenceLockReason={planningLocked ? planningLockReason : ""}
                    sequenceLocked={planningLocked}
                  />
                )}

                {activeTab === "driver" && (
                  <SedulurDalanMonitoringPanel fleetOrderId={fleetOrder.id} />
                )}
              </FleetDetailTabErrorBoundary>
            </div>
          </form>
        )}
      </section>

      {emergencyAmendmentOpen && fleetOrder && (
        <FleetOrderEmergencyAmendmentDialog
          fleetOrder={fleetOrder}
          onApplied={handleEmergencyAmendmentApplied}
          onClose={() => setEmergencyAmendmentOpen(false)}
        />
      )}

      {operationalOverrideOpen && fleetOrder && (
        <FleetOrderOperationalOverrideDialog
          fleetOrder={fleetOrder}
          onApplied={handleOperationalOverrideApplied}
          onClose={() => setOperationalOverrideOpen(false)}
        />
      )}

      {operationalCorrection && fleetOrder && (
        <FleetOrderOperationalCorrectionDialog
          fleetOrder={fleetOrder}
          initialDocument={operationalCorrection.initialDocument || null}
          onApplied={handleOperationalCorrectionApplied}
          onClose={() => setOperationalCorrection(null)}
        />
      )}

      {manualCompletionOpen && fleetOrder && (
        <FleetOrderManualCompletionDialog
          fleetOrder={fleetOrder}
          onClose={() => setManualCompletionOpen(false)}
          onCompleted={handleManualCompletion}
        />
      )}

      {cancelConfirmOpen && fleetOrder && (
        <ConfirmDialog
          busy={cancelling}
          description={`Batalkan ${fleetOrder.foNumber}? Pesanan yang masih pending akan dikembalikan menjadi belum dijadwalkan.`}
          onCancel={() => setCancelConfirmOpen(false)}
          onConfirm={confirmCancellation}
          title="Batalkan Fleet Order"
        />
      )}

      {deleteConfirmOpen && fleetOrder && (
        <Dialog onClose={() => !deleting && setDeleteConfirmOpen(false)} title="Hapus Fleet Order">
          <div className="dialog-card__body fleet-delete-confirmation">
            <div className="error-banner" role="alert">
              <strong>{fleetOrder.status === "SCHEDULED" ? "Perhatian" : "Peringatan kuat"}</strong>
              <span>Fleet Order akan disembunyikan dari operasi aktif. Relasi SO/DO dan penugasan aktif akan dilepas, tetapi seluruh riwayat dan foto tetap disimpan.</span>
            </div>
            <dl className="fleet-order-readonly">
              <div><dt>Nomor FO</dt><dd>{fleetOrder.foNumber}</dd></div>
              <div><dt>Driver</dt><dd>{fleetOrder.driverName || "—"}</dd></div>
              <div><dt>Co-Driver</dt><dd>{fleetOrder.coDriverName || "—"}</dd></div>
              <div><dt>Kendaraan</dt><dd>{fleetOrder.vehiclePlate || "—"}</dd></div>
              <div><dt>Tanggal Berangkat</dt><dd>{formatDate(fleetOrder.departureDate)}</dd></div>
              <div><dt>Estimasi Pulang</dt><dd>{formatDate(fleetOrder.estimatedReturnDate)}</dd></div>
              <div><dt>Rute</dt><dd>{getFleetRouteName(fleetOrder)}</dd></div>
              <div><dt>Stop / Dokumen</dt><dd>{fleetOrder.stopCount || 0} / {fleetOrder.documentCount || fleetOrder.salesOrderCount || 0}</dd></div>
            </dl>
            <label>
              <span>Alasan penghapusan</span>
              <textarea
                autoFocus
                minLength={5}
                onChange={(event) => setDeletionReason(event.target.value)}
                placeholder="Jelaskan alasan Fleet Order dihapus"
                required
                rows={3}
                value={deletionReason}
              />
            </label>
          </div>
          <footer className="dialog-card__actions">
            <button className="secondary-button" disabled={deleting} onClick={() => setDeleteConfirmOpen(false)} type="button">Batal</button>
            <button className="danger-button--outline" disabled={deleting || deletionReason.trim().length < 5} onClick={confirmDeletion} type="button">
              {deleting ? "Menghapus..." : "Hapus Fleet Order"}
            </button>
          </footer>
        </Dialog>
      )}

      {comparison?.hasChanges && (
        <Dialog onClose={() => setComparison(null)} title="Perubahan Pesanan">
          <div className="dialog-card__body">
            <p>Perubahan ditemukan. Snapshot FO hanya diperbarui setelah konfirmasi.</p>

            <div className="fleet-snapshot-check-summary">
              <span>
                <strong>{comparison.summary?.eligibleCount || 0}</strong>
                SO diperiksa
              </span>
              <span>
                <strong>{comparison.summary?.protectedCount || 0}</strong>
                SO dilewati karena DO sudah dibuat
              </span>
              {(comparison.deliveryOrderReconciliation?.repairedCount || 0) > 0 && (
                <span>
                  <strong>
                    {comparison.deliveryOrderReconciliation.repairedCount}
                  </strong>
                  relasi DO diperbaiki
                </span>
              )}
            </div>

            <div className="snapshot-comparison">
              {safeArray(comparison.orders)
                .filter((order) => order.hasChanges)
                .map((order) => (
                  <div key={order.salesOrderId}>
                    <strong>{order.documentNumber || order.soNumber}</strong>

                    <span>
                      Berat: {formatWeight(order.snapshot?.totalWeightKg)} →{" "}
                      {formatWeight(order.latest?.totalWeightKg)}
                    </span>

                    <span>
                      Volume: {formatVolume(order.snapshot?.totalVolumeM3)} →{" "}
                      {formatVolume(order.latest?.totalVolumeM3)}
                    </span>

                    {order.shipping?.hasChanges && (
                      <>
                        <span>
                          Area: {order.shipping.snapshot?.resolvedArea || "—"} →{" "}
                          {order.shipping.latest?.resolvedArea || "—"}
                        </span>

                        <span>
                          Rute:{" "}
                          {order.shipping.snapshot?.resolvedRouteName ||
                            order.shipping.snapshot?.resolvedRouteCode ||
                            "—"}{" "}
                          →{" "}
                          {order.shipping.latest?.resolvedRouteName ||
                            order.shipping.latest?.resolvedRouteCode ||
                            "—"}
                        </span>
                      </>
                    )}
                  </div>
                ))}
            </div>
          </div>

          <footer className="dialog-card__actions">
            <button className="secondary-button" onClick={() => setComparison(null)} type="button">
              Batal
            </button>

            <button
              className="primary-button"
              disabled={updatingSnapshot || !canUpdateSnapshot}
              onClick={confirmSnapshotUpdate}
              title={
                canUpdateSnapshot
                  ? "Perbarui snapshot Fleet Order."
                  : planningLockReason
              }
              type="button"
            >
              {updatingSnapshot ? "Memperbarui..." : "Update Snapshot FO"}
            </button>
          </footer>
        </Dialog>
      )}

      {resetSequenceConfirmOpen && fleetOrder && (
        <ConfirmDialog
          busy={sequenceResetting}
          busyLabel="Mereset..."
          confirmLabel="Reset Urutan"
          description={`Reset urutan stop ${fleetOrder.foNumber}? Semua nomor urutan tersimpan akan dihapus dan daftar kembali ke urutan default.`}
          onCancel={() => setResetSequenceConfirmOpen(false)}
          onConfirm={confirmResetSequence}
          title="Reset Urutan Stop"
        />
      )}

      {removingOrder && fleetOrder && (
        <ConfirmDialog
          busy={removing}
          busyLabel="Mengeluarkan..."
          confirmLabel="Keluarkan"
          description={`${removingOrder.documentNumber || removingOrder.soNumber} milik ${
            removingOrder.customerName || "pelanggan"
          } akan dikeluarkan dari ${fleetOrder.foNumber}. Lanjutkan?`}
          onCancel={() => setRemovingOrder(null)}
          onConfirm={confirmRemoveOrder}
          title="Keluarkan dari FO"
        />
      )}
    </>
  );
}

function getFleetReadinessStatus(fleetOrder) {
  return String(fleetOrder?.readiness?.status || "UNKNOWN").toUpperCase();
}

function getFleetRouteName(fleetOrder) {
  return fleetOrder?.foRouteName || fleetOrder?.routeName || "Belum ditentukan";
}

function getFleetStopCount(fleetOrder) {
  return Number(fleetOrder?.operationalSummary?.stopCount ?? fleetOrder?.stopCount ?? 0);
}

function getFleetSalesOrderCount(fleetOrder) {
  return Number(
    fleetOrder?.operationalSummary?.documentCount ??
      fleetOrder?.operationalSummary?.salesOrderCount ??
      fleetOrder?.documentCount ??
      fleetOrder?.salesOrderCount ??
      0,
  );
}

function FleetOverviewCard({
  description,
  icon = "document",
  label,
  tone = "neutral",
  value,
}) {
  return (
    <article className={`fleet-overview-card fleet-overview-card--${tone}`}>
      <span className="fleet-overview-card__icon" aria-hidden="true">
        <Icon name={icon} size={18} />
      </span>
      <div className="fleet-overview-card__content">
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function FleetListStack({ primary, secondary }) {
  return (
    <div className="fleet-list-stack">
      <strong>{primary || "—"}</strong>
      <span>{secondary || "—"}</span>
    </div>
  );
}

function FleetOrderMobileCard({
  fleetOrder,
  onOpen,
  onShare,
  shareLoading = false,
}) {
  const readiness = fleetOrder.readiness || {};
  const readinessStatus = getFleetReadinessStatus(fleetOrder);
  const canShare = getFleetSalesOrderCount(fleetOrder) > 0;
  const readinessDetail =
    readinessStatus === "NOT_APPLICABLE"
      ? "FO selesai atau dibatalkan"
      : `${readiness.blockerCount || 0} blocker · ${
          readiness.warningCount || 0
        } perhatian`;

  return (
    <article
      className={[
        "fleet-mobile-card",
        readinessStatus === "BLOCKED" ? "fleet-mobile-card--blocked" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <header className="fleet-mobile-card__header">
        <div className="fleet-mobile-card__identity">
          <span>Fleet Order</span>
          <strong>{fleetOrder.foNumber || "Nomor FO belum tersedia"}</strong>
          <small>Tanggal FO {formatDate(fleetOrder.foDate)}</small>
        </div>

        <FleetStatusBadge status={fleetOrder.status} />
      </header>

      <div className="fleet-mobile-card__route-row">
        <RouteBadge name={getFleetRouteName(fleetOrder)} />
      </div>

      <dl className="fleet-mobile-card__details">
        <div className="fleet-mobile-card__detail fleet-mobile-card__detail--wide">
          <dt>Depot asal</dt>
          <dd>{fleetOrder.depot?.depotName || "Belum dipilih"}</dd>
          <small>{fleetOrder.depot?.depotCode || "Titik awal dan kembali"}</small>
        </div>

        <div className="fleet-mobile-card__detail fleet-mobile-card__detail--wide">
          <dt>Perjalanan</dt>
          <dd>
            {formatDate(fleetOrder.departureDate)} – {formatDate(fleetOrder.estimatedReturnDate)}
          </dd>
        </div>

        <div className="fleet-mobile-card__detail">
          <dt>Armada</dt>
          <dd>{fleetOrder.vehiclePlate || "Belum dipilih"}</dd>
          <small>{fleetOrder.vehicleType || "Jenis kendaraan belum tersedia"}</small>
        </div>

        <div className="fleet-mobile-card__detail">
          <dt>Tim</dt>
          <dd>{fleetOrder.driverName || "Driver belum dipilih"}</dd>
          <small>
            {fleetOrder.coDriverName
              ? `Co-driver: ${fleetOrder.coDriverName}`
              : "Co-driver belum dipilih"}
          </small>
        </div>

        <div className="fleet-mobile-card__detail">
          <dt>Stop / Dokumen</dt>
          <dd>
            {getFleetStopCount(fleetOrder)} stop · {getFleetSalesOrderCount(fleetOrder)} dokumen
          </dd>
        </div>

        <div className="fleet-mobile-card__detail">
          <dt>Muatan</dt>
          <dd>{formatWeight(fleetOrder.totalWeightKg)}</dd>
          <small>{formatVolume(fleetOrder.totalVolumeM3)}</small>
        </div>
      </dl>

      <div className="fleet-mobile-card__readiness">
        <ReadinessBadge compact readiness={readiness} />
        <span>{readinessDetail}</span>
      </div>

      <footer className="fleet-mobile-card__actions">
        <button className="primary-button" onClick={onOpen} type="button">
          Buka Fleet Order
        </button>

        <button
          className="secondary-button"
          disabled={shareLoading || !canShare}
          onClick={onShare}
          type="button"
        >
          {shareLoading ? "Memuat…" : "Bagikan"}
        </button>

        {!canShare && (
          <small>Bagikan tersedia setelah Fleet Order memiliki pesanan aktif.</small>
        )}
      </footer>
    </article>
  );
}

export function FleetOrdersPage() {
  const initialListStateRef = useRef(readFleetOrderListReturnState());
  const initialListState = initialListStateRef.current || {};
  const initialFilters = initialListState.filters || {
    search: "",
    departureDate: "",
    route: "",
    depot: "",
    readiness: "",
    visibility: "ACTIVE",
  };

  const [fleetOrders, setFleetOrders] = useState([]);
  const [serverStatusCounts, setServerStatusCounts] = useState({
    SCHEDULED: 0,
    COMPLETED: 0,
    CANCELLED: 0,
  });

  const [searchDraft, setSearchDraft] = useState(
    initialListState.searchDraft ?? initialFilters.search ?? "",
  );

  const [departureDateDraft, setDepartureDateDraft] = useState(
    initialListState.departureDateDraft ?? initialFilters.departureDate ?? "",
  );

  const [routeDraft, setRouteDraft] = useState(
    initialListState.routeDraft ?? initialFilters.route ?? "",
  );

  const [depotDraft, setDepotDraft] = useState(
    initialListState.depotDraft ?? initialFilters.depot ?? "",
  );

  const [readinessDraft, setReadinessDraft] = useState(
    initialListState.readinessDraft ?? initialFilters.readiness ?? "",
  );
  const [visibilityDraft, setVisibilityDraft] = useState(
    initialListState.visibilityDraft ?? initialFilters.visibility ?? "ACTIVE",
  );
  const [activeStatus, setActiveStatus] = useState(
    initialListState.activeStatus ?? "SCHEDULED",
  );
  const [currentPage, setCurrentPage] = useState(
    Math.max(Number(initialListState.currentPage) || 1, 1),
  );
  const [pageSize, setPageSize] = useState(
    [10, 25, 50].includes(Number(initialListState.pageSize))
      ? Number(initialListState.pageSize)
      : 10,
  );
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [filters, setFilters] = useState(initialFilters);

  const initialDetailLocationRef = useRef(readFleetDetailLocation());
  const [selectedId, setSelectedId] = useState(initialDetailLocationRef.current.id);
  const [selectedTab, setSelectedTab] = useState(initialDetailLocationRef.current.tab);
  const listScrollYRef = useRef(
    Number(initialListState.scrollY ?? window.history.state?.listScrollY) || 0,
  );

  const [shareFleetOrder, setShareFleetOrder] = useState(null);

  const [shareLoadingId, setShareLoadingId] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [message, setMessage] = useState("");

  const load = useCallback(async (visibility = "ACTIVE") => {
    setLoading(true);
    setError("");

    try {
      const payload = await getFleetOrders({
        limit: 200,
        visibility,
        status: activeStatus,
      });

      setFleetOrders(payload.fleetOrders || []);
      setServerStatusCounts((current) => ({
        ...current,
        ...(payload.statusCounts || {}),
      }));
      setLastUpdatedAt(new Date());
    } catch (requestError) {
      const incidentId = requestError.correlationId
        ? ` ID gangguan: ${requestError.correlationId}.`
        : "";
      setError(
        `Data Fleet Order belum dapat dimuat. ${requestError.message}${incidentId}`,
      );
    } finally {
      setLoading(false);
    }
  }, [activeStatus]);

  useEffect(() => {
    load(initialFilters.visibility || "ACTIVE");
  }, [load, initialFilters.visibility]);

  useEffect(() => {
    let restoreFrameId = null;

    function syncDetailLocation() {
      if (!window.location.pathname.startsWith("/fleet-orders")) {
        if (restoreFrameId !== null) {
          window.cancelAnimationFrame(restoreFrameId);
          restoreFrameId = null;
        }
        return;
      }

      const nextLocation = readFleetDetailLocation();
      setSelectedId(nextLocation.id);
      setSelectedTab(nextLocation.tab);

      if (nextLocation.id) {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } else {
        clearFleetOrderListReturnState();
        restoreFrameId = window.requestAnimationFrame(() => {
          restoreFrameId = null;
          window.scrollTo({
            top: listScrollYRef.current,
            left: 0,
            behavior: "auto",
          });
        });
      }
    }

    window.addEventListener("route-planning:location-changed", syncDetailLocation);
    return () => {
      window.removeEventListener("route-planning:location-changed", syncDetailLocation);
      if (restoreFrameId !== null) {
        window.cancelAnimationFrame(restoreFrameId);
      }
    };
  }, []);

  const routeOptions = useMemo(() => {
    const values = new Map();

    fleetOrders.forEach((fleetOrder) => {
      const name = getFleetRouteName(fleetOrder);

      if (name && name !== "Belum ditentukan") {
        values.set(name.toLowerCase(), name);
      }
    });

    return [...values.values()].sort((left, right) => left.localeCompare(right, "id"));
  }, [fleetOrders]);

  const depotOptions = useMemo(() => {
    const values = new Map();

    fleetOrders.forEach((fleetOrder) => {
      const depot = fleetOrder.depot || null;
      const id = depot?.id || fleetOrder.depotId || null;
      const name = depot?.depotName || "Depot belum dipilih";

      if (id) {
        values.set(String(id), {
          id: String(id),
          name,
          code: depot?.depotCode || "",
        });
      }
    });

    return [...values.values()].sort((left, right) =>
      left.name.localeCompare(right.name, "id"),
    );
  }, [fleetOrders]);

  const filteredFleetOrders = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return fleetOrders.filter((fleetOrder) => {
      if (search) {
        const haystack = [
          fleetOrder.foNumber,
          fleetOrder.driverName,
          fleetOrder.coDriverName,
          fleetOrder.vehiclePlate,
          fleetOrder.vehicleType,
          fleetOrder.foRouteName,
          fleetOrder.routeName,
          fleetOrder.depot?.depotName,
          fleetOrder.depot?.depotCode,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (filters.departureDate && fleetOrder.departureDate !== filters.departureDate) {
        return false;
      }

      if (filters.route && getFleetRouteName(fleetOrder) !== filters.route) {
        return false;
      }

      if (
        filters.depot &&
        String(fleetOrder.depot?.id || fleetOrder.depotId || "") !== String(filters.depot)
      ) {
        return false;
      }

      if (String(fleetOrder.status || "").toUpperCase() !== activeStatus) {
        return false;
      }

      if (filters.readiness && getFleetReadinessStatus(fleetOrder) !== filters.readiness) {
        return false;
      }

      return true;
    });
  }, [activeStatus, fleetOrders, filters]);

  const listRows = useMemo(
    () =>
      filteredFleetOrders.map((fleetOrder) => ({
        ...fleetOrder,

        stopCount: getFleetStopCount(fleetOrder),

        salesOrderCount: getFleetSalesOrderCount(fleetOrder),

        readinessSort:
          {
            BLOCKED: 4,
            ATTENTION: 3,
            READY: 2,
            NOT_APPLICABLE: 1,
            UNKNOWN: 0,
          }[getFleetReadinessStatus(fleetOrder)] ?? 0,
      })),
    [filteredFleetOrders],
  );

  const { sortedItems, sort, toggleSort } = useSortableData(
    listRows,
    initialListState.sort?.key || "departureDate",
    initialListState.sort?.direction || "desc",
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * pageSize;
  const paginatedItems = useMemo(
    () => sortedItems.slice(pageStartIndex, pageStartIndex + pageSize),
    [pageSize, pageStartIndex, sortedItems],
  );
  const pageFrom = sortedItems.length ? pageStartIndex + 1 : 0;
  const pageTo = Math.min(pageStartIndex + pageSize, sortedItems.length);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const overview = useMemo(() => {
    const result = {
      total: filteredFleetOrders.length,

      ready: 0,
      attention: 0,
      blocked: 0,
      notApplicable: 0,
    };

    filteredFleetOrders.forEach((fleetOrder) => {
      const status = getFleetReadinessStatus(fleetOrder);

      if (status === "READY") {
        result.ready += 1;
      } else if (status === "ATTENTION") {
        result.attention += 1;
      } else if (status === "BLOCKED") {
        result.blocked += 1;
      } else if (status === "NOT_APPLICABLE") {
        result.notApplicable += 1;
      }
    });

    return result;
  }, [filteredFleetOrders]);

  const statusCounts = useMemo(
    () => ({
      SCHEDULED: Number(serverStatusCounts.SCHEDULED) || 0,
      COMPLETED: Number(serverStatusCounts.COMPLETED) || 0,
      CANCELLED: Number(serverStatusCounts.CANCELLED) || 0,
    }),
    [serverStatusCounts],
  );

  const activeStatusLabel = STATUS_LABELS[activeStatus] || "Fleet Order";

  const activeFilterLabels = useMemo(() => {
    const labels = [];

    if (filters.search) labels.push(`Pencarian: ${filters.search}`);
    if (filters.departureDate) labels.push(`Berangkat: ${formatDate(filters.departureDate)}`);
    if (filters.route) labels.push(`Rute: ${filters.route}`);
    if (filters.depot) {
      const depot = depotOptions.find((item) => item.id === String(filters.depot));
      labels.push(`Depot: ${depot?.name || filters.depot}`);
    }
    if (filters.readiness) {
      labels.push(`Kesiapan: ${READINESS_LABELS[filters.readiness] || filters.readiness}`);
    }
    if (filters.visibility === "INCLUDE_DELETED") {
      labels.push("Visibilitas: Aktif + Dihapus");
    } else if (filters.visibility === "DELETED_ONLY") {
      labels.push("Visibilitas: Hanya Dihapus");
    } else {
      labels.push("Visibilitas: Aktif");
    }

    return labels;
  }, [depotOptions, filters]);

  const formattedLastUpdatedAt = lastUpdatedAt
    ? new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(lastUpdatedAt)
    : "";

  function publishCommittedLocation(url, state) {
    window.dispatchEvent(
      new CustomEvent("route-planning:location-committed", {
        detail: {
          state,
          url,
        },
      }),
    );
  }

  function openFleetOrderDetails(id) {
    if (!id) return;

    listScrollYRef.current = window.scrollY;
    saveFleetOrderListReturnState({
      activeStatus,
      currentPage: safeCurrentPage,
      pageSize,
      departureDateDraft,
      filters,
      readinessDraft,
      routeDraft,
      depotDraft,
      scrollY: listScrollYRef.current,
      searchDraft,
      sort,
      visibilityDraft,
    });

    const nextTab = "summary";
    const nextUrl = buildFleetDetailUrl(id, nextTab);

    const nextState = {
      routePlanningView: "fleet-order-detail",
      fleetOrderId: String(id),
      fleetOrderTab: nextTab,
      fromFleetOrderList: true,
      listScrollY: listScrollYRef.current,
    };

    window.history.pushState(nextState, "", nextUrl);
    publishCommittedLocation(nextUrl, nextState);

    setSelectedId(String(id));
    setSelectedTab(nextTab);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  function closeFleetOrderDetails() {
    clearFleetOrderListReturnState();

    const nextState = {
      routePlanningView: "fleet-order-list",
      restoredFromFleetOrderDetail: true,
      listScrollY: listScrollYRef.current,
    };

    window.history.pushState(nextState, "", "/fleet-orders");
    publishCommittedLocation("/fleet-orders", nextState);
    setSelectedId(null);
    setSelectedTab("summary");

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: listScrollYRef.current, left: 0, behavior: "auto" });
    });
  }

  function changeFleetOrderDetailTab(tab) {
    const nextTab = normalizeFleetDetailTab(tab);

    if (!selectedId || selectedTab === nextTab) return;

    const nextState = {
      ...(window.history.state || {}),
      routePlanningView: "fleet-order-detail",
      fleetOrderId: String(selectedId),
      fleetOrderTab: nextTab,
    };

    const nextUrl = buildFleetDetailUrl(selectedId, nextTab);
    window.history.pushState(nextState, "", nextUrl);
    publishCommittedLocation(nextUrl, nextState);
    setSelectedTab(nextTab);
  }

  function applyFilters(event) {
    event.preventDefault();
    setCurrentPage(1);

    setFilters({
      search: searchDraft.trim(),

      departureDate: departureDateDraft,

      route: routeDraft,

      depot: depotDraft,

      readiness: readinessDraft,

      visibility: visibilityDraft,
    });
    load(visibilityDraft);
  }

  function clearFilters() {
    setCurrentPage(1);
    setSearchDraft("");
    setDepartureDateDraft("");
    setRouteDraft("");
    setDepotDraft("");
    setReadinessDraft("");
    setVisibilityDraft("ACTIVE");

    setFilters({
      search: "",
      departureDate: "",
      route: "",
      depot: "",
      readiness: "",
      visibility: "ACTIVE",
    });
    load("ACTIVE");
  }

  async function openMonitoringShare(fleetOrder) {
    if (!fleetOrder?.id) return;

    setMessage("");
    setError("");

    if (Array.isArray(fleetOrder.stops)) {
      const availability = canShareFleetOrderMonitoring(fleetOrder);

      if (!availability.allowed) {
        setError(availability.reason || "Monitoring Fleet Order belum dapat dibuat.");
        return;
      }

      setShareFleetOrder(fleetOrder);
      return;
    }

    setShareLoadingId(fleetOrder.id);

    try {
      const payload = await getFleetOrder(fleetOrder.id);
      const detail = payload.fleetOrder;
      const availability = canShareFleetOrderMonitoring(detail);

      if (!availability.allowed) {
        setError(availability.reason || "Monitoring Fleet Order belum dapat dibuat.");
        return;
      }

      setShareFleetOrder(detail);
    } catch (requestError) {
      setError(`Data Fleet Order tidak dapat dimuat. ${requestError.message}`);
    } finally {
      setShareLoadingId(null);
    }
  }

  async function handleUpdated(nextMessage) {
    setMessage(nextMessage);
    await load(filters.visibility);
  }

  if (selectedId) {
    return (
      <main className="fleet-page fleet-detail-route-page">
        <FleetOrderDetailWorkspace
          id={selectedId}
          initialTab={selectedTab}
          onClose={closeFleetOrderDetails}
          onShare={openMonitoringShare}
          onTabChange={changeFleetOrderDetailTab}
          onUpdated={handleUpdated}
        />

        {shareFleetOrder && (
          <FleetOrderShareModal
            fleetOrder={shareFleetOrder}
            onClose={() => setShareFleetOrder(null)}
          />
        )}
      </main>
    );
  }

  return (
    <main className="fleet-page fleet-control-page">
      <FleetOrdersCommandHeader
        contextLabel={activeStatusLabel}
        loading={loading}
        onRefresh={() => load(filters.visibility)}
        subtitle="Pantau kesiapan armada, perjalanan, stop, pesanan, dan muatan operasional."
        totalCount={
          activeFilterLabels.length
            ? filteredFleetOrders.length
            : statusCounts[activeStatus] || filteredFleetOrders.length
        }
        updatedAt={formattedLastUpdatedAt}
      />

      <div className="fleet-workspace-shell">
        <FleetOrdersWorkspaceTabs
          activeTab={activeStatus}
          counts={statusCounts}
          onChange={(nextStatus) => {
            setActiveStatus(nextStatus);
            setCurrentPage(1);
          }}
        />

        {(message || error) && (
          <div className="fleet-workspace-feedback">
            {message && <div className="notice-banner">{message}</div>}
            {error && (
              <div className="error-banner" role="alert">
                {error}
              </div>
            )}
          </div>
        )}

        <FleetOrdersKpiStrip label={`Ringkasan Fleet Order ${activeStatusLabel}`}>
          <FleetOverviewCard
            description={
              overview.notApplicable
                ? `${overview.notApplicable} FO tidak memerlukan pemeriksaan kesiapan`
                : "Jumlah Fleet Order sesuai filter aktif"
            }
            icon="document"
            label="Total FO"
            value={overview.total}
          />

          <FleetOverviewCard
            description="Tidak memiliki blocker maupun perhatian"
            icon="check"
            label="Siap"
            tone="success"
            value={overview.ready}
          />

          <FleetOverviewCard
            description="Masih memiliki perhatian yang perlu ditinjau"
            icon="warning"
            label="Perlu Perhatian"
            tone="warning"
            value={overview.attention}
          />

          <FleetOverviewCard
            description="Memiliki blocker operasional"
            icon="close"
            label="Terblokir"
            tone="danger"
            value={overview.blocked}
          />
        </FleetOrdersKpiStrip>

        <FleetOrdersFilterWorkbench
          activeFilters={
            <FleetActiveFilterSummary
              labels={activeFilterLabels}
              onClear={clearFilters}
              showClear={false}
            />
          }
        >
          <ResponsiveFilterPanel
            description="Saring Fleet Order berdasarkan perjalanan dan kesiapan operasional."
            label="Filter Fleet Order"
          >
            <form className="fleet-filters fleet-filters--operational" onSubmit={applyFilters}>
              <label className="search-field">
                <Icon name="search" size={16} />

                <input
                  aria-label="Cari Fleet Order"
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder="Cari nomor FO, driver, co-driver, nopol, atau rute"
                  value={searchDraft}
                />
              </label>

              <input
                aria-label="Tanggal berangkat"
                onChange={(event) => setDepartureDateDraft(event.target.value)}
                title="Tanggal berangkat"
                type="date"
                value={departureDateDraft}
              />

              <select
                aria-label="Rute Fleet Order"
                onChange={(event) => setRouteDraft(event.target.value)}
                value={routeDraft}
              >
                <option value="">Semua rute</option>

                {routeOptions.map((route) => (
                  <option key={route} value={route}>
                    {route}
                  </option>
                ))}
              </select>

              <select
                aria-label="Depot asal Fleet Order"
                onChange={(event) => setDepotDraft(event.target.value)}
                value={depotDraft}
              >
                <option value="">Semua depot</option>

                {depotOptions.map((depot) => (
                  <option key={depot.id} value={depot.id}>
                    {depot.name}{depot.code ? ` · ${depot.code}` : ""}
                  </option>
                ))}
              </select>

              <select
                aria-label="Status kesiapan"
                onChange={(event) => setReadinessDraft(event.target.value)}
                value={readinessDraft}
              >
                <option value="">Semua kesiapan</option>
                <option value="READY">Siap</option>
                <option value="ATTENTION">Perlu perhatian</option>
                <option value="BLOCKED">Terblokir</option>
                <option value="NOT_APPLICABLE">Tidak berlaku</option>
              </select>

              <select
                aria-label="Visibilitas Fleet Order"
                onChange={(event) => setVisibilityDraft(event.target.value)}
                value={visibilityDraft}
              >
                <option value="ACTIVE">Aktif</option>
                <option value="INCLUDE_DELETED">Termasuk yang Dihapus</option>
                <option value="DELETED_ONLY">Hanya yang Dihapus</option>
              </select>

              <div className="fleet-filter-actions">
                <button className="secondary-button" type="submit">
                  <Icon name="filter" size={15} />
                  Terapkan
                </button>

                <button className="text-button" onClick={clearFilters} type="button">
                  Bersihkan
                </button>
              </div>
            </form>
          </ResponsiveFilterPanel>
        </FleetOrdersFilterWorkbench>

        <section className="records-panel fleet-data-surface">
          <FleetOrdersListHeader
            context={
              <span className="fleet-data-surface__context">
                <Icon name="database" size={13} />
                Sumber: Database Route Planning
              </span>
            }
            description="Buka Fleet Order untuk meninjau kesiapan, stop, muatan, rute, dan progres driver."
            title={`Daftar Fleet Order ${activeStatusLabel}`}
          />

          <div className="table-wrap fleet-list-wrap">
            <div
              aria-label={`Daftar Fleet Order ${activeStatusLabel}`}
              className="fleet-mobile-list"
            >
              {!loading &&
                paginatedItems.map((fleetOrder) => (
                  <FleetOrderMobileCard
                    fleetOrder={fleetOrder}
                    key={fleetOrder.id}
                    onOpen={() => openFleetOrderDetails(fleetOrder.id)}
                    onShare={() => openMonitoringShare(fleetOrder)}
                    shareLoading={shareLoadingId === fleetOrder.id}
                  />
                ))}
            </div>

            <table className="fleet-list-table fleet-list-table--operational responsive-card-table">
              <thead>
                <tr>
                  <SortableHeader column="foNumber" sort={sort} toggleSort={toggleSort}>
                    Nomor FO
                  </SortableHeader>

                  <SortableHeader column="departureDate" sort={sort} toggleSort={toggleSort}>
                    Perjalanan
                  </SortableHeader>

                  <SortableHeader column="foRouteName" sort={sort} toggleSort={toggleSort}>
                    Rute FO
                  </SortableHeader>

                  <SortableHeader column="vehiclePlate" sort={sort} toggleSort={toggleSort}>
                    Armada
                  </SortableHeader>

                  <SortableHeader column="driverName" sort={sort} toggleSort={toggleSort}>
                    Tim
                  </SortableHeader>

                  <SortableHeader column="stopCount" sort={sort} toggleSort={toggleSort}>
                    Stop / Dokumen
                  </SortableHeader>

                  <SortableHeader column="totalWeightKg" sort={sort} toggleSort={toggleSort}>
                    Muatan
                  </SortableHeader>

                  <SortableHeader column="readinessSort" sort={sort} toggleSort={toggleSort}>
                    Kesiapan
                  </SortableHeader>

                  <SortableHeader column="status" sort={sort} toggleSort={toggleSort}>
                    Status
                  </SortableHeader>

                  <th className="sticky-action-cell">Aksi</th>
                </tr>
              </thead>

              <tbody>
                {!loading &&
                  paginatedItems.map((fleetOrder) => {
                    const readiness = fleetOrder.readiness || {};
                    const readinessStatus = getFleetReadinessStatus(fleetOrder);

                    return (
                      <tr
                        className={[
                          "clickable-table-row",
                          readinessStatus === "BLOCKED" ? "fleet-list-row--blocked" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        key={fleetOrder.id}
                        onClick={(event) => {
                          if (!isInteractiveTarget(event.target)) {
                            openFleetOrderDetails(fleetOrder.id);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (
                            (event.key === "Enter" || event.key === " ") &&
                            !isInteractiveTarget(event.target)
                          ) {
                            event.preventDefault();
                            openFleetOrderDetails(fleetOrder.id);
                          }
                        }}
                        role="button"
                        tabIndex="0"
                      >
                        <td>
                          <FleetListStack
                            primary={fleetOrder.foNumber}
                            secondary={`Tanggal FO ${formatDate(fleetOrder.foDate)}`}
                          />
                        </td>

                        <td>
                          <FleetListStack
                            primary={formatDate(fleetOrder.departureDate)}
                            secondary={`s.d. ${formatDate(fleetOrder.estimatedReturnDate)}`}
                          />
                        </td>

                        <td>
                          <div className="fleet-route-depot-stack">
                            <RouteBadge name={getFleetRouteName(fleetOrder)} />
                            <span>
                              Dari: {fleetOrder.depot?.depotName || "Depot belum dipilih"}
                            </span>
                          </div>
                        </td>

                        <td>
                          <FleetListStack
                            primary={fleetOrder.vehiclePlate}
                            secondary={fleetOrder.vehicleType || "Jenis kendaraan belum tersedia"}
                          />
                        </td>

                        <td>
                          <FleetListStack
                            primary={fleetOrder.driverName}
                            secondary={
                              fleetOrder.coDriverName
                                ? `Co-driver: ${fleetOrder.coDriverName}`
                                : "Co-driver belum dipilih"
                            }
                          />
                        </td>

                        <td>
                          <FleetListStack
                            primary={`${fleetOrder.stopCount} stop`}
                            secondary={`${fleetOrder.documentCount ?? fleetOrder.salesOrderCount ?? 0} dokumen`}
                          />
                        </td>

                        <td>
                          <FleetListStack
                            primary={formatWeight(fleetOrder.totalWeightKg)}
                            secondary={formatVolume(fleetOrder.totalVolumeM3)}
                          />
                        </td>

                        <td>
                          <div className="fleet-readiness-cell">
                            <ReadinessBadge compact readiness={readiness} />
                            <span>
                              {readinessStatus === "NOT_APPLICABLE"
                                ? "FO selesai atau dibatalkan"
                                : `${readiness.blockerCount || 0} blocker · ${
                                    readiness.warningCount || 0
                                  } perhatian`}
                            </span>
                          </div>
                        </td>

                        <td>
                          <FleetStatusBadge status={fleetOrder.status} />
                        </td>

                        <td className="sticky-action-cell">
                          <div className="fleet-list-actions">
                            <button
                              className="table-action-button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openFleetOrderDetails(fleetOrder.id);
                              }}
                              type="button"
                            >
                              Buka
                            </button>

                            <button
                              className="table-action-button table-action-button--secondary"
                              disabled={
                                shareLoadingId === fleetOrder.id ||
                                getFleetSalesOrderCount(fleetOrder) <= 0
                              }
                              onClick={(event) => {
                                event.stopPropagation();
                                openMonitoringShare(fleetOrder);
                              }}
                              title={
                                getFleetSalesOrderCount(fleetOrder) > 0
                                  ? "Bagikan monitoring Fleet Order ke WhatsApp."
                                  : "Fleet Order belum memiliki pesanan aktif."
                              }
                              type="button"
                            >
                              {shareLoadingId === fleetOrder.id ? "Memuat..." : "Bagikan"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>

            {loading && <div className="fleet-list-state">Memuat Fleet Order…</div>}

            {!loading && filteredFleetOrders.length === 0 && (
              <div className="fleet-list-state">Belum ada Fleet Order sesuai filter.</div>
            )}
          </div>

          {!loading && (
            <footer className="fleet-list-footer">
              <div className="fleet-list-footer__summary">
                <span>
                  Menampilkan <strong>{pageFrom}–{pageTo}</strong> dari{" "}
                  <strong>{sortedItems.length}</strong> FO berstatus {activeStatusLabel}.
                </span>
                <small>
                  Total seluruh status: {fleetOrders.length} Fleet Order. Pindah status melalui tab Terjadwal, Selesai, atau Dibatalkan.
                </small>
              </div>

              <div className="fleet-list-pagination" aria-label="Navigasi halaman Fleet Order">
                <label>
                  <span>Baris</span>
                  <select
                    aria-label="Jumlah Fleet Order per halaman"
                    onChange={(event) => {
                      setPageSize(Number(event.target.value));
                      setCurrentPage(1);
                    }}
                    value={pageSize}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                </label>
                <button
                  className="secondary-button"
                  disabled={safeCurrentPage <= 1}
                  onClick={() => setCurrentPage((value) => Math.max(1, value - 1))}
                  type="button"
                >
                  Sebelumnya
                </button>
                <span className="fleet-list-pagination__position">
                  {safeCurrentPage} / {totalPages}
                </span>
                <button
                  className="secondary-button"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={() => setCurrentPage((value) => Math.min(totalPages, value + 1))}
                  type="button"
                >
                  Berikutnya
                </button>
              </div>
            </footer>
          )}
        </section>
      </div>

      {shareFleetOrder && (
        <FleetOrderShareModal
          fleetOrder={shareFleetOrder}
          onClose={() => setShareFleetOrder(null)}
        />
      )}
    </main>
  );
}
